import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import firebase, { auth, db } from "../firebase";
import { assignmentStageLabel, isCollectedOrDoneStage, isTailorPackedStage, normalizeAssignmentStage } from "./campus_assignment_stages";
import { fetchBookingsLight } from "./campus_bookings_light";
import { effectiveStatus } from "./campus_stickers";
import "./campus_packing.css";

const COL_ASSIGN = "CampusAssignments";
const COL_ACTIONS = "CampusActionLog";
const CONFIG_DOC = "CampusConfig";
/** Sirf suggestion — carton ka size ab aap decide karte ho (25 / 30 / jo bhi). */
const DEFAULT_CARTON_TARGET = 25;
/** Same as Campus Orders — undo code ke bina last action reverse nahi. */
const UNDO_CODE = "3624";

const GARMENT_LABEL = { coat: "Coat", pant: "Pant", shirt: "Shirt", vest_coat: "Vest Coat", tie: "Tie" };
const RECEIVE_TYPES = ["pant", "shirt", "coat", "tie", "vest_coat"];

const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const fabricKey = (s) => String(s || "").trim().toLowerCase();
const garmentKey = (bookingId, itemId) => `${bookingId}__${itemId}`;
const stripId = (obj) => { if (!obj) return null; const { id, ...rest } = obj; return rest; };
const logAction = (label, ops) =>
    db.collection(COL_ACTIONS).add({ label, ops, at: Date.now() })
        .catch((e) => console.log("action log", e));

const isBoxPacked = (a) => normalizeAssignmentStage(a?.stage) === "box_packed";
const isReceived = (a) => isCollectedOrDoneStage(a?.stage);
const isPartialCartonKind = (a) => String(a?.packCartonKind || "") === "partial";

/** Partial carton mein ja sakta hai? Coat: received YA tailor-packed. Baaki: received. */
const canPackIntoPartial = (a, itemType) => {
    if (!a || isBoxPacked(a)) return false;
    if (isReceived(a)) return true;
    if (itemType === "coat") {
        const st = normalizeAssignmentStage(a.stage);
        return isTailorPackedStage(st) || st === "ready";
    }
    return false;
};

const isBookingFullyPacked = (b, assignments) => {
    if (!b) return false;
    const deliveredIds = b.delivered_item_ids || [];
    const items = (b.active_items || []).filter((item) => !deliveredIds.includes(item.id));
    if (!items.length) return false;
    return items.every((item) => isBoxPacked(assignments[garmentKey(b.id, item.id)]));
};

/** Kuch pieces pack ho gaye, poora order nahi. */
const isBookingPartlyPacked = (b, assignments) => {
    if (!b) return false;
    const deliveredIds = b.delivered_item_ids || [];
    const items = (b.active_items || []).filter((item) => !deliveredIds.includes(item.id));
    if (!items.length) return false;
    const packed = items.filter((item) => isBoxPacked(assignments[garmentKey(b.id, item.id)])).length;
    return packed > 0 && packed < items.length;
};

const bookingStatus = (b) => String(b?.status || "booked").toLowerCase();
/** Delivered / cancelled orders should not appear in the packing queue.
 *  "Delivered" ka matlab effective status hai (delivered_item_ids + coat rule):
 *  agar coat de diya aur baaki items missing hain to order DELIVERED (status
 *  "missing") maana jaata hai — customer maal le ja chuka, ab wo pack nahi hoga.
 *  Sirf raw status dekhne se aise order missing-items ke saath queue me atak
 *  jaate the. */
const isPackingExcluded = (b) => {
    if (bookingStatus(b) === "cancelled") return true;
    const eff = effectiveStatus(b).status;
    return eff === "delivered" || eff === "missing" || eff === "cancelled";
};

const itemArrived = (a) => isReceived(a) || isBoxPacked(a);

/** Tie ka hisaab alag chalta hai (tailor pipeline me nahi aata), isliye
 *  packing ke missing / full-detail report me tie nahi ginte. */
const DETAIL_SKIP_TYPES = new Set(["tie"]);
const NO_FABRIC = "— fabric not set —";
const NOT_ISSUED = "not issued";
const countsInDetail = (itemType) => !DETAIL_SKIP_TYPES.has(String(itemType || "").toLowerCase());

/** Missing piece abhi kahan hai — packing wale ki bhasha me. */
const pendingWhereLabel = (stage, tailor) => {
    if (!tailor) return "Not issued";
    const st = normalizeAssignmentStage(stage);
    if (st === "packed") return "Tailor packed — pickup pending";
    if (st === "ready") return "Ready at tailor";
    if (st === "stitching") return "Stitching";
    if (st === "cutting") return "Cutting";
    if (st === "pending") return "Issued — not started";
    return assignmentStageLabel(st);
};

const bookingItemsForReport = (b, assignments) => {
    const deliveredIds = b.delivered_item_ids || [];
    return (b.active_items || [])
        .filter((item) => !deliveredIds.includes(item.id))
        .map((item) => {
            const key = garmentKey(b.id, item.id);
            const a = assignments[key] || {};
            return {
                key,
                itemId: item.id,
                itemType: item.type,
                itemName: item.name || GARMENT_LABEL[item.type] || item.type,
                assignment: a,
                arrived: itemArrived(a),
                packed: isBoxPacked(a),
            };
        });
};

const searchNorm = (s) => String(s || "").toLowerCase().replace(/\s+/g, "");

/* ── Stall drive filter (same as Campus Orders) — worker galat campus ka
   invoice na utha le, isliye packing queue bhi drive-wise. ── */
const DRIVE_GAP_DAYS = 2;
const orderDateOf = (b) => String(b.order_date || b.created_at || "").slice(0, 10);
const DRIVE_STORE_KEY = "packDriveFilter";
const fmtDriveRange = (from, to) => {
    const f = new Date(from), t = new Date(to);
    if (Number.isNaN(f.getTime()) || Number.isNaN(t.getTime())) return from || "—";
    const opt = { day: "numeric", month: "short" };
    if (from === to) return f.toLocaleDateString("en-IN", opt);
    if (f.getMonth() === t.getMonth() && f.getFullYear() === t.getFullYear()) {
        return `${f.getDate()}–${t.toLocaleDateString("en-IN", opt)}`;
    }
    return `${f.toLocaleDateString("en-IN", opt)} – ${t.toLocaleDateString("en-IN", opt)}`;
};
const loadStoredDrive = () => {
    try {
        const d = JSON.parse(window.localStorage.getItem(DRIVE_STORE_KEY));
        if (d && typeof d === "object") {
            return { campus: d.campus || "", from: d.from || "", to: d.to || "" };
        }
    } catch (e) { /* corrupt store — ignore */ }
    return { campus: "", from: "", to: "" };
};

/** Invoice ke end ka number — IITKAN56 → "56" */
const invoiceNumericSuffix = (invoiceNo) => {
    const m = String(invoiceNo || "").match(/(\d+)\s*$/);
    return m ? m[1] : "";
};

const normInvoiceNum = (s) => {
    const n = parseInt(String(s || "").replace(/\D/g, ""), 10);
    return Number.isFinite(n) ? String(n) : "";
};

/** Carton chips — invoice A→Z, number wise (IITKAN8, IITKAN12, IITKAN195). */
const sortInvoicesAlpha = (a, b) =>
    String(a.invoiceNo || "").localeCompare(String(b.invoiceNo || ""), undefined, { numeric: true, sensitivity: "base" });

/** "1" → sirf IITKAN1, na ki 10/11/19. "iitkan19" → exact number. */
const matchesInvoiceQuery = (invoiceNo, q) => {
    const qTrim = String(q || "").trim();
    if (!qTrim) return true;
    const qLower = qTrim.toLowerCase();
    const invNum = normInvoiceNum(invoiceNumericSuffix(invoiceNo));

    if (/^\d+$/.test(qTrim)) {
        return invNum === normInvoiceNum(qTrim);
    }
    const kanMatch = qLower.match(/^iitkan\s*(\d+)$/);
    if (kanMatch) {
        return invNum === normInvoiceNum(kanMatch[1]);
    }
    return String(invoiceNo || "").toLowerCase().includes(qLower);
};

const invoiceMatchesSearch = (inv, q) => {
    if (!q) return true;
    const qTrim = q.trim();
    const qLower = qTrim.toLowerCase();
    const qFlat = searchNorm(qTrim);

    if (/^\d+$/.test(qTrim) || /^iitkan\s*\d+$/i.test(qTrim)) {
        return matchesInvoiceQuery(inv.invoiceNo, qTrim);
    }

    const hay = [
        inv.invoiceNo,
        inv.customerName,
        inv.campus,
        ...inv.activeItems.map((it) => it.fabric),
        ...inv.activeItems.map((it) => it.shade),
        ...inv.activeItems.map((it) => it.itemName),
    ]
        .filter(Boolean)
        .map((s) => String(s).toLowerCase());
    return hay.some((h) => h.includes(qLower) || searchNorm(h).includes(qFlat));
};
const COLOR_HEX = {
    "dark blue": "#14213d", "sky blue": "#7ec3e8", "light blue": "#9cc7f0", "navy": "#14213d",
    "dark green": "#14532d", "light green": "#7fc98a", "olive": "#708238",
    "black": "#15151a", "white": "#f4f4f0", "grey": "#8a8f98", "gray": "#8a8f98", "charcoal": "#36393f",
    "blue": "#1f4fd8", "maroon": "#7b1f2b", "wine": "#722f37", "red": "#d92c3c", "green": "#1f7b3e",
    "cream": "#f1e6c8", "beige": "#d9c7a7", "brown": "#6b4a2a", "coffee": "#5a3d2b", "tan": "#c9a173",
    "pink": "#e78ab0", "purple": "#6c3fa8", "violet": "#7a5fc0", "yellow": "#e8c132", "mustard": "#d4a017",
    "orange": "#e07b2a", "peach": "#f2b28c", "silver": "#c0c0c0", "gold": "#c9a45c", "ivory": "#f3ecd8",
};
const COLOR_KEYS = Object.keys(COLOR_HEX).sort((a, b) => b.length - a.length);
const swatchFor = (label) => {
    const t = String(label || "").toLowerCase();
    if (!t) return null;
    for (const k of COLOR_KEYS) if (t.includes(k)) return COLOR_HEX[k];
    return null;
};

/** Confirm-dialog ke andar item chips ki list. */
/** IITMAD102 -> "IITMAD" halka + "102" gehra. Sau chips scan karte waqt
 *  aankh seedhe number par jaati hai, poora code padhna nahi padta. */
function InvNo({ value }) {
    const raw = String(value == null ? "" : value).trim() || "\u2014";
    const m = raw.match(/^(.*?)(\d+)$/);
    if (!m || !m[2]) return <span className="inv-num">{raw}</span>;
    return (
        <>
            <span className="inv-pre">{m[1]}</span>
            <span className="inv-num">{m[2]}</span>
        </>
    );
}

function PackItemList({ label, items, tone }) {
    if (!items.length) return null;
    return (
        <div className={`pack-cf-list pack-cf-list--${tone}`}>
            <span className="pack-cf-list-label">{label}</span>
            <div className="pack-cf-chips">
                {items.map((it) => (
                    <span className="pack-cf-chip" key={it.key || it.itemId}>
                        {it.itemName || GARMENT_LABEL[it.itemType] || it.itemType}
                    </span>
                ))}
            </div>
        </div>
    );
}

function Campus_packing() {
    /** Sab bookings (delivered/cancelled samet) — drive chips isi se bante hain,
     *  warna deliver ho chuki drive bar se gayab ho jaati thi aur uspar atki
     *  hui selection se packing list chup-chaap khaali dikhti thi. */
    const [allBookings, setAllBookings] = useState([]);
    const [assignments, setAssignments] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState("");
    const [fabricFilter, setFabricFilter] = useState("");
    const [selectedId, setSelectedId] = useState("");
    const [ticks, setTicks] = useState(() => new Set());
    const [missingModal, setMissingModal] = useState(false);
    const [missingSearch, setMissingSearch] = useState("");
    /** Full detail panel ka garment filter — "" = sab. */
    const [detailType, setDetailType] = useState("");
    const [missingLog, setMissingLog] = useState([]);
    // Carton config PER-DRIVE hai (har stall drive ke apne cartons):
    // driveCartons[driveKey] = { cartonOpen: [], cartonSealed: [], cartonTarget }.
    // cfgCarton sirf purane (legacy) data ko sahi dikhane ke liye bacha hai.
    const [cfgCarton, setCfgCarton] = useState(1);           // legacy global (All / purana)
    const [driveCartons, setDriveCartons] = useState({});    // { driveKey: {...} }
    /** Hold: pack to ho gaya lekin shak hai (koi piece bahar dikh gaya).
     *  Aisi invoice carton me nahi jaayegi jab tak check karke clear na ho.
     *  CampusConfig/packing.packHolds = { INVOICE: { at, by } } */
    const [packHolds, setPackHolds] = useState({});
    const [toast, setToast] = useState(null);              // { msg, kind, canUndo }
    const [confirmState, setConfirmState] = useState(null); // { title, body, confirmLabel, tone, resolve }
    const toastTimer = useRef(null);
    const skipAutoSelectRef = useRef(false);
    const searchInputRef = useRef(null);
    const [searchFresh, setSearchFresh] = useState(false);
    const [receiveType, setReceiveType] = useState("pant");
    /** null = pehle poochho: pair / single / partial / carton */
    const [packMode, setPackMode] = useState(null);
    /** Stall drive filter — last selection localStorage me, taaki reopen pe bhi lage. */
    const [drive, setDrive] = useState(loadStoredDrive);
    const [driveCustom, setDriveCustom] = useState(false);

    /** Load fail hone par screen "koi order nahi" dikhati thi — asli wajah
     *  (permission / network) kahin nahi dikhti thi. Ab wo yahan aati hai. */
    const [loadError, setLoadError] = useState(null);

    const loadBookings = useCallback(async () => {
        setLoading(true);
        const prep = (list) => [...list]
            .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        try {
            // Light REST read — base64 photos skip (~3MB vs ~164MB), slow net par bhi fast
            setAllBookings(prep(await fetchBookingsLight()));
            setLoadError(null);
        } catch (eLight) {
            console.log("packing light bookings failed, full SDK read", eLight);
            try {
                const snap = await db.collection("maviinci_bookings").get();
                setAllBookings(prep(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
                setLoadError(null);
            } catch (e) {
                console.log("packing bookings", e);
                const denied = String(e?.code || "").includes("permission-denied")
                    || /HTTP 40[13]/.test(String(eLight?.message || ""));
                setLoadError({
                    denied,
                    detail: String(e?.message || eLight?.message || e || "unknown error"),
                });
            }
        } finally {
            setLoading(false);
        }
    }, []);

    /** Kis account se login hai — permission error samajhne ke liye. */
    const [authEmail, setAuthEmail] = useState(() => auth.currentUser?.email || "");
    useEffect(() => auth.onAuthStateChanged((u) => setAuthEmail(u?.email || "")), []);

    /** Packing queue — delivered / missing / cancelled nikal kar. */
    const bookings = useMemo(() => allBookings.filter((b) => !isPackingExcluded(b)), [allBookings]);

    useEffect(() => {
        loadBookings();
        const u1 = db.collection(COL_ASSIGN).onSnapshot(
            (s) => setAssignments(Object.fromEntries(s.docs.map((d) => [d.id, { id: d.id, ...d.data() }]))),
            (e) => console.log("packing assign", e)
        );
        const u2 = db.collection(CONFIG_DOC).doc("packing").onSnapshot(
            (d) => {
                const data = d.data() || {};
                setMissingLog(Array.isArray(data.missingLog) ? data.missingLog : []);
                setCfgCarton(num(data.currentCarton) || 1);
                setDriveCartons(data.driveCartons && typeof data.driveCartons === "object" ? data.driveCartons : {});
                setPackHolds(data.packHolds && typeof data.packHolds === "object" ? data.packHolds : {});
            },
            (e) => console.log("packing config", e)
        );
        return () => { u1(); u2(); };
    }, [loadBookings]);

    useEffect(() => {
        try { window.localStorage.setItem(DRIVE_STORE_KEY, JSON.stringify(drive)); } catch (e) { /* private mode */ }
    }, [drive]);

    // ── Drive filter: campus + date clusters (gap > 2 din = alag drive) ──
    // NOTE: clusters SAARE orders se bante hain (delivered samet). Pehle ye
    // sirf packing queue se bante the — jis drive ka maal deliver ho jaata wo
    // chip hi gayab ho jaati thi, lekin localStorage me selection bachi rehti
    // thi → screen khaali aur wajah kahin nahi dikhti thi.
    const drives = useMemo(() => {
        const pendingIds = new Set(bookings.map((b) => b.id));
        const byCampus = new Map();
        allBookings.forEach((b) => {
            const d = orderDateOf(b);
            if (!d) return;
            const c = b.campus || "—";
            if (!byCampus.has(c)) byCampus.set(c, []);
            byCampus.get(c).push({ d, pending: pendingIds.has(b.id) });
        });
        const out = [];
        byCampus.forEach((rows, campus) => {
            const uniq = [...new Set(rows.map((r) => r.d))].sort();
            if (!uniq.length) return;
            const flush = (from, to) => {
                const inRange = rows.filter((r) => r.d >= from && r.d <= to);
                out.push({
                    campus, from, to,
                    count: inRange.length,
                    pending: inRange.filter((r) => r.pending).length,
                });
            };
            let start = uniq[0], prev = uniq[0];
            for (let i = 1; i < uniq.length; i++) {
                const gapDays = (new Date(uniq[i]) - new Date(prev)) / 86400000;
                if (gapDays > DRIVE_GAP_DAYS) { flush(start, prev); start = uniq[i]; }
                prev = uniq[i];
            }
            flush(start, prev);
        });
        return out.sort((a, b) => (b.to || "").localeCompare(a.to || ""));
    }, [allBookings, bookings]);

    const driveOn = !!(drive.campus || drive.from || drive.to);
    /** Chuni hui drive chips me hai? (custom range / purani selection me nahi hoti) */
    const driveInChips = useMemo(
        () => drives.some((d) => d.campus === drive.campus && d.from === drive.from && d.to === drive.to),
        [drives, drive]
    );

    /** Packing queue (pair / single / partial / missing) — sirf is drive ke orders.
     *  Cartons physical hote hain, unki history filter nahi hoti. */
    const driveBookings = useMemo(() => {
        if (!driveOn) return bookings;
        return bookings.filter((b) => {
            if (drive.campus && b.campus !== drive.campus) return false;
            const d = orderDateOf(b);
            if (drive.from && (!d || d < drive.from)) return false;
            if (drive.to && (!d || d > drive.to)) return false;
            return true;
        });
    }, [bookings, drive, driveOn]);

    // ── Carton state (packing se ALAG) ────────────────────────────────────
    // Naya system: packing sirf "pack" karta hai — koi carton number apne aap
    // nahi lagta. Carton mode me aap khud carton number chunte ho, packed list
    // me se invoices uthake usme daalte ho (25 / 30 / jitna chaaho), phir seal.
    // Legacy: purane drives me partial-kind cartons ki alag series thi — wo
    // history me alag section me read-only dikhti hai, chhedi nahi jaati.
    const driveBookingIds = useMemo(() => new Set(driveBookings.map((b) => b.id)), [driveBookings]);
    const driveKey = useMemo(
        () => (driveOn ? `${drive.campus || ""}|${drive.from || ""}|${drive.to || ""}`.replace(/[^A-Za-z0-9]+/g, "_") : "__all__"),
        [driveOn, drive]
    );
    const driveCfg = useMemo(() => driveCartons?.[driveKey] || {}, [driveCartons, driveKey]);

    /** Ek carton me kitne invoice — sirf aapka suggestion, koi limit nahi. */
    const cartonTarget = useMemo(() => {
        const n = num(driveCfg.cartonTarget);
        return n > 0 ? n : DEFAULT_CARTON_TARGET;
    }, [driveCfg]);

    /** Har packed invoice ka ek row — carton lagi ho ya na ho. */
    const packedRows = useMemo(() => {
        const bookingById = Object.fromEntries(bookings.map((b) => [b.id, b]));
        const byInvoice = new Map();
        Object.values(assignments).forEach((a) => {
            if (!isBoxPacked(a)) return;
            if (!driveBookingIds.has(a.bookingId)) return;
            const b = bookingById[a.bookingId];
            if (!b || isPackingExcluded(b)) return;
            const deliveredIds = b.delivered_item_ids || [];
            const activeIds = new Set(
                (b.active_items || [])
                    .filter((item) => !deliveredIds.includes(item.id))
                    .map((item) => item.id)
            );
            if (a.itemId && activeIds.size && !activeIds.has(a.itemId)) return; // orphan assignment

            const invoiceNo = a.invoiceNo || a.bookingId || "—";
            if (!byInvoice.has(invoiceNo)) {
                byInvoice.set(invoiceNo, {
                    invoiceNo,
                    bookingId: a.bookingId,
                    customerName: a.customerName || "",
                    pieces: 0,
                    at: 0,
                    carton: null,
                    legacyPartial: false,
                    cartonPieces: 0,
                    loosePieces: 0,
                    looseTypes: [],
                    complete: isBookingFullyPacked(b, assignments),
                    missing: [],
                });
            }
            const rec = byInvoice.get(invoiceNo);
            rec.pieces += 1;
            rec.at = Math.max(rec.at, num(a.packAt) || num(a.packingAt) || 0);
            if (a.customerName) rec.customerName = a.customerName;
            if (a.packCarton != null) {
                const c = num(a.packCarton);
                rec.carton = rec.carton == null ? c : Math.max(rec.carton, c);
                rec.cartonPieces += 1;
                if (isPartialCartonKind(a)) rec.legacyPartial = true;
            } else {
                rec.loosePieces += 1;
                rec.looseTypes.push(GARMENT_LABEL[a.itemType] || a.itemType || "piece");
            }
            if (Array.isArray(a.partialMissing) && a.partialMissing.length) rec.missing = a.partialMissing;
        });
        return [...byInvoice.values()];
    }, [assignments, bookings, driveBookingIds]);

    /** Pack ho chuka lekin abhi kisi carton me nahi — yahi se carton bharoge. */
    const looseRows = useMemo(
        () => packedRows.filter((r) => r.carton == null).sort(sortInvoicesAlpha),
        [packedRows]
    );

    /** Invoice carton me ja chuki hai, lekin uska koi piece BAAD me pack hua
     *  aur bina carton ke pada hai. Aisi invoice "awaiting carton" list me
     *  nahi aati (carton to lagi hai), isliye chup-chaap gayab lagti thi. */
    const splitRows = useMemo(
        () => packedRows
            .filter((r) => r.carton != null && r.loosePieces > 0 && !r.legacyPartial)
            .sort(sortInvoicesAlpha),
        [packedRows]
    );

    const isHeld = useCallback((invoiceNo) => !!packHolds?.[invoiceNo], [packHolds]);
    /** Rok kar rakhi hui invoices — upar alag patti me dikhti hain. */
    const heldRows = useMemo(
        () => looseRows.filter((r) => isHeld(r.invoiceNo)),
        [looseRows, isHeld]
    );

    /** Purane system ke partial cartons — sirf record ke liye. */
    const legacyPartialCartons = useMemo(() => {
        const map = new Map();
        packedRows.forEach((r) => {
            if (!r.legacyPartial || r.carton == null) return;
            if (!map.has(r.carton)) map.set(r.carton, []);
            map.get(r.carton).push(r);
        });
        return [...map.entries()]
            .map(([cartonNo, items]) => ({
                cartonNo,
                items: [...items].sort(sortInvoicesAlpha),
                pieces: items.reduce((n, e) => n + e.pieces, 0),
            }))
            .sort((a, b) => b.cartonNo - a.cartonNo);
    }, [packedRows]);

    /** Cartons = jinme maal hai + jo khaali banaye gaye. Sealed list config me. */
    const cartons = useMemo(() => {
        const byNo = new Map();
        packedRows.forEach((r) => {
            if (r.carton == null || r.legacyPartial) return;
            if (!byNo.has(r.carton)) byNo.set(r.carton, []);
            byNo.get(r.carton).push(r);
        });
        (driveCfg.cartonOpen || []).forEach((n) => {
            const c = num(n);
            if (c > 0 && !byNo.has(c)) byNo.set(c, []);
        });
        const sealedList = Array.isArray(driveCfg.cartonSealed) ? driveCfg.cartonSealed.map(num) : null;
        const sealedSet = new Set(sealedList || []);
        // Legacy fallback: purane system me "currentCarton se neeche sab sealed" tha.
        const legacyCut = sealedList
            ? 0
            : num(driveCfg.currentCarton != null ? driveCfg.currentCarton : (driveKey === "__all__" ? cfgCarton : 0));
        return [...byNo.entries()]
            .map(([cartonNo, items]) => ({
                cartonNo,
                items: [...items].sort(sortInvoicesAlpha),
                pieces: items.reduce((n, e) => n + e.pieces, 0),
                sealed: sealedList ? sealedSet.has(cartonNo) : (legacyCut > 0 && cartonNo < legacyCut),
            }))
            .sort((a, b) => a.cartonNo - b.cartonNo);
    }, [packedRows, driveCfg, driveKey, cfgCarton]);

    const openCartons = useMemo(() => cartons.filter((c) => !c.sealed), [cartons]);
    const sealedCartons = useMemo(
        () => cartons.filter((c) => c.sealed).sort((a, b) => b.cartonNo - a.cartonNo),
        [cartons]
    );
    const inCartonCount = useMemo(() => cartons.reduce((n, c) => n + c.items.length, 0), [cartons]);
    const cartonProgressPct = useMemo(() => {
        const total = inCartonCount + looseRows.length;
        return total > 0 ? Math.min(100, (inCartonCount / total) * 100) : 0;
    }, [inCartonCount, looseRows.length]);
    /** Pehla khaali number — naya carton banate waqt default. */
    const nextFreeCartonNo = useMemo(() => {
        const used = new Set(cartons.map((c) => c.cartonNo));
        let n = 1;
        while (used.has(n)) n += 1;
        return n;
    }, [cartons]);

    /** Adhoore order jinke ready pieces ab pack ho sakte hain (coat cover hona
     *  chahiye). Carton yahan nahi lagta — wo Carton mode ka kaam hai. */
    const partialEligibleList = useMemo(() => {
        const out = [];
        driveBookings.forEach((b) => {
            if (isPackingExcluded(b)) return;
            if (isBookingFullyPacked(b, assignments)) return;
            const deliveredIds = b.delivered_item_ids || [];
            const items = (b.active_items || [])
                .filter((item) => !deliveredIds.includes(item.id))
                .map((item) => {
                    const key = garmentKey(b.id, item.id);
                    const a = assignments[key] || {};
                    return {
                        key,
                        itemId: item.id,
                        itemType: item.type,
                        itemName: item.name || GARMENT_LABEL[item.type] || item.type,
                        fabric: item.type === "tie" ? (b[`${item.id}_color`] || "") : (b[`${item.id}_fabric`] || ""),
                        shade: b[`${item.id}_shade`] || "",
                        assignment: a,
                    };
                });
            if (!items.length) return;
            if (!items.some((it) => it.itemType === "coat")) return;

            // Abhi pack ho sakne wale pieces (jo pehle se packed hain wo nahi).
            const toPack = items.filter((it) => canPackIntoPartial(it.assignment, it.itemType));
            if (!toPack.length) return;

            // Coat ya to pehle se packed ho ya ab pack ho raha ho.
            const coatCovered = items.some(
                (it) => it.itemType === "coat" && (isBoxPacked(it.assignment) || toPack.includes(it))
            );
            if (!coatCovered) return;

            const missing = items.filter(
                (it) => !isBoxPacked(it.assignment) && !canPackIntoPartial(it.assignment, it.itemType)
            );
            if (!missing.length) return; // sab ready → pair packing

            out.push({
                bookingId: b.id,
                invoiceNo: b.invoice_no || "—",
                customerName: b.customer_name || "—",
                campus: b.campus || "—",
                toPack,
                missing,
                missingLabels: missing.map((it) => it.itemName || GARMENT_LABEL[it.itemType] || it.itemType),
            });
        });
        return out.sort(sortInvoicesAlpha);
    }, [driveBookings, assignments]);

    const invoices = useMemo(() => {
        const out = [];
        driveBookings.forEach((b) => {
            if (isPackingExcluded(b)) return;
            const deliveredIds = b.delivered_item_ids || [];
            const items = (b.active_items || [])
                .filter((item) => !deliveredIds.includes(item.id)) // deliver ho chuka — pack nahi karna
                .map((item) => {
                const key = garmentKey(b.id, item.id);
                const a = assignments[key] || {};
                return {
                    key,
                    itemId: item.id,
                    itemType: item.type,
                    itemName: item.name || GARMENT_LABEL[item.type] || item.type,
                    fabric: item.type === "tie" ? (b[`${item.id}_color`] || "") : (b[`${item.id}_fabric`] || ""),
                    shade: b[`${item.id}_shade`] || "",
                    stage: a.stage || "",
                    assignment: a,
                };
            });
            if (!items.length) return;
            const active = items.filter((it) => !isBoxPacked(it.assignment));
            if (!active.length) return;
            const activeReceived = active.filter((it) => isReceived(it.assignment)).length;
            const allReceived = active.length > 0 && active.every((it) => isReceived(it.assignment));
            const status = allReceived ? "ready" : activeReceived > 0 ? "progress" : "open";
            out.push({
                bookingId: b.id,
                invoiceNo: b.invoice_no || "—",
                customerName: b.customer_name || "—",
                campus: b.campus || "—",
                items,
                activeItems: active,
                receivedCount: activeReceived,
                activeTotal: active.length,
                totalCount: items.length,
                status,
            });
        });
        return out;
    }, [driveBookings, assignments]);

    const fabricOptions = useMemo(() => {
        const s = new Set();
        invoices.forEach((inv) => inv.activeItems.forEach((it) => { if (it.fabric) s.add(it.fabric); }));
        return [...s].sort((a, b) => a.localeCompare(b));
    }, [invoices]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const fk = fabricKey(fabricFilter);
        return invoices.filter((inv) => {
            if (fk && !inv.activeItems.some((it) => fabricKey(it.fabric) === fk)) return false;
            return invoiceMatchesSearch(inv, q);
        });
    }, [invoices, search, fabricFilter]);

    const readyList = useMemo(() => filtered.filter((i) => i.status === "ready"), [filtered]);
    const progressList = useMemo(() => filtered.filter((i) => i.status === "progress" || i.status === "open"), [filtered]);

    /** Type-first receive: bache hue pieces jo abhi received/box_packed nahi. */
    const pendingPiecesByType = useMemo(() => {
        const byType = Object.fromEntries(RECEIVE_TYPES.map((t) => [t, []]));
        driveBookings.forEach((b) => {
            if (isPackingExcluded(b)) return;
            const deliveredIds = b.delivered_item_ids || [];
            (b.active_items || []).forEach((item) => {
                if (deliveredIds.includes(item.id)) return;
                const key = garmentKey(b.id, item.id);
                const a = assignments[key] || {};
                if (isReceived(a)) return;
                const type = item.type || "";
                if (!byType[type]) byType[type] = [];
                byType[type].push({
                    key,
                    itemId: item.id,
                    itemType: type,
                    itemName: item.name || GARMENT_LABEL[type] || type,
                    fabric: type === "tie" ? (b[`${item.id}_color`] || "") : (b[`${item.id}_fabric`] || ""),
                    shade: b[`${item.id}_shade`] || "",
                    bookingId: b.id,
                    invoiceNo: b.invoice_no || "—",
                    customerName: b.customer_name || "—",
                    campus: b.campus || "—",
                    assignment: a,
                });
            });
        });
        Object.values(byType).forEach((list) => list.sort(sortInvoicesAlpha));
        return byType;
    }, [driveBookings, assignments]);

    const receiveTypePieces = pendingPiecesByType[receiveType] || [];
    const receiveTypeCounts = useMemo(
        () => Object.fromEntries(RECEIVE_TYPES.map((t) => [t, (pendingPiecesByType[t] || []).length])),
        [pendingPiecesByType]
    );

    const selected = useMemo(
        () => invoices.find((i) => i.bookingId === selectedId) || null,
        [invoices, selectedId]
    );

    const selectedMissing = useMemo(() => {
        if (!selected) return [];
        const anyPacked = selected.activeItems.some((it) => isBoxPacked(it.assignment));
        return selected.activeItems.filter((it) => {
            if (isBoxPacked(it.assignment)) return false;
            if (anyPacked) return true;
            return !ticks.has(it.key);
        });
    }, [selected, ticks]);

    const selectInvoice = useCallback((bookingId) => {
        setSelectedId(bookingId);
        const inv = invoices.find((i) => i.bookingId === bookingId);
        if (!inv) { setTicks(new Set()); return; }
        setTicks(new Set(inv.activeItems.map((it) => it.key)));
    }, [invoices]);

    useEffect(() => {
        if (!filtered.length) {
            if (selectedId) {
                setSelectedId("");
                setTicks(new Set());
            }
            return;
        }
        const stillVisible = selectedId && filtered.some((i) => i.bookingId === selectedId);
        if (stillVisible) return;

        if (!selectedId) {
            if (skipAutoSelectRef.current) {
                skipAutoSelectRef.current = false;
                return;
            }
            const q = search.trim();
            if (q && filtered.length === 1) {
                selectInvoice(filtered[0].bookingId);
                setSearchFresh(false);
                return;
            }
            if (q) return;
            const pick = filtered.find((i) => i.status === "ready") || filtered[0];
            if (pick) selectInvoice(pick.bookingId);
            return;
        }

        const pick = filtered.find((i) => i.status === "ready") || filtered[0];
        selectInvoice(pick.bookingId);
    }, [filtered, selectedId, selectInvoice, search]);

    const resetForNextPack = useCallback(() => {
        skipAutoSelectRef.current = true;
        setSearchFresh(true);
        setSearch("");
        setFabricFilter("");
        setSelectedId("");
        setTicks(new Set());
        window.setTimeout(() => {
            searchInputRef.current?.focus();
            searchInputRef.current?.select();
        }, 80);
    }, []);

    const onPackSearchChange = (e) => {
        setSearchFresh(false);
        skipAutoSelectRef.current = false;
        setSearch(e.target.value);
    };

    const toggleTick = (key, it) => {
        if (isBoxPacked(it.assignment)) return;
        setTicks((prev) => {
            const n = new Set(prev);
            n.has(key) ? n.delete(key) : n.add(key);
            return n;
        });
    };

    const tickedItems = useMemo(() => {
        if (!selected) return [];
        return selected.activeItems.filter((it) => ticks.has(it.key));
    }, [selected, ticks]);

    const allActiveTicked = selected
        ? selected.activeItems.length > 0 && selected.activeItems.every((it) => ticks.has(it.key))
        : false;

    const canReceive = tickedItems.some((it) => !isReceived(it.assignment));
    const canPartialPack = tickedItems.length > 0 && !allActiveTicked;
    const canPack = allActiveTicked;

    const persistConfig = async (patch) => {
        await db.collection(CONFIG_DOC).doc("packing").set(patch, { merge: true });
    };
    // Is drive ke carton counter save karo (nested map, siblings safe rehte hain).
    const persistDriveCarton = (patch) =>
        persistConfig({ driveCartons: { [driveKey]: patch } });

    const showToast = useCallback((msg, kind = "ok", { canUndo = false } = {}) => {
        setToast({ msg, kind, canUndo });
        if (toastTimer.current) clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToast(null), canUndo ? 8000 : 4000);
    }, []);
    useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);


    // Promise-based in-app confirm (browser window.confirm ki jagah).
    const askConfirm = (opts) => new Promise((resolve) => setConfirmState({ ...opts, resolve }));
    const resolveConfirm = (val) => setConfirmState((c) => { if (c) c.resolve(val); return null; });

    const undoLast = async () => {
        if (saving) return;
        const code = window.prompt("Enter undo code:");
        if (code === null) return;
        if (code.trim() !== UNDO_CODE) {
            showToast("Wrong undo code.", "err");
            return;
        }
        try {
            const snap = await db.collection(COL_ACTIONS).orderBy("at", "desc").limit(1).get();
            if (snap.empty) {
                showToast("Nothing to undo.", "warn");
                return;
            }
            const docSnap = snap.docs[0];
            const { label, ops = [] } = docSnap.data();
            const ok = await askConfirm({
                title: "Undo last packing action?",
                confirmLabel: "Undo",
                tone: "danger",
                body: <p>Last action: <b>{label || "—"}</b></p>,
            });
            if (!ok) return;
            setSaving(true);
            const batch = db.batch();
            ops.forEach((op) => {
                if (!op?.col || !op?.id) return;
                const ref = db.collection(op.col).doc(op.id);
                if (op.prev == null) batch.delete(ref);
                else if (op.merge) batch.set(ref, op.prev, { merge: true });
                else batch.set(ref, op.prev);
            });
            batch.delete(docSnap.ref);
            await batch.commit();
            await loadBookings();
            setToast(null);
            showToast(`Undone: ${label}`, "ok");
        } catch (e) {
            console.log("packing undo", e);
            showToast("Could not undo. Please try again.", "err");
        } finally {
            setSaving(false);
        }
    };

    // Carton ka koi kaam yahan nahi — packing sirf stage badalti hai.
    // Carton number baad me Carton mode se lagta hai.
    const writeItems = async (items, stage, labelPrefix, { missingLabels = null, promoteReady = false } = {}) => {
        const undoOps = [];
        const batch = db.batch();
        const now = Date.now();
        const packing = stage === "box_packed";
        items.forEach((it) => {
            const prev = assignments[it.key];
            const prevSnap = prev ? stripId({ ...prev }) : null;
            undoOps.push({ col: COL_ASSIGN, id: it.key, prev: prevSnap });
            const base = prev || {};
            batch.set(db.collection(COL_ASSIGN).doc(it.key), {
                ...base,
                bookingId: selected.bookingId,
                invoiceNo: selected.invoiceNo,
                itemId: it.itemId,
                itemName: it.itemName,
                itemType: it.itemType,
                fabric: it.fabric,
                shade: it.shade || "",
                customerName: selected.customerName,
                campus: selected.campus,
                tailor: base.tailor || "",
                stage,
                packingAt: now,
                updatedAt: now,
                ...(packing ? { packAt: now } : {}),
                ...(packing && Array.isArray(missingLabels) ? { partialMissing: missingLabels } : {}),
            }, { merge: true });
        });

        // 📦 Poori invoice pack → booking READY (same undoOps mein — ek Undo se dono reverse).
        let promotedReady = false;
        if (packing && promoteReady) {
            const packedKeys = new Set(items.map((it) => it.key));
            const allPacked = (selected.items || []).every(
                (it) => packedKeys.has(it.key) || isBoxPacked(it.assignment)
            );
            if (allPacked) {
                const raw = bookings.find((b) => b.id === selected.bookingId);
                const st = bookingStatus(raw);
                if (st === "booked") {
                    // merge:true — undo pe poori booking replace nahi, sirf status wapas
                    undoOps.push({
                        col: "maviinci_bookings",
                        id: selected.bookingId,
                        prev: raw ? { status: raw.status || "booked" } : null,
                        merge: true,
                    });
                    batch.set(db.collection("maviinci_bookings").doc(selected.bookingId), {
                        status: "ready",
                        ready_at: now,
                    }, { merge: true });
                    promotedReady = true;
                }
            }
        }

        await batch.commit();
        await logAction(`${labelPrefix} · ${selected.invoiceNo} (${items.length} pcs)`, undoOps);
        if (promotedReady) {
            setAllBookings((prev) => prev.map((b) =>
                b.id === selected.bookingId ? { ...b, status: "ready", ready_at: now } : b));
        }
    };

    // "All campuses" me pack karne se carton global/doosri drive me chala jaata
    // hai (Delhi order Kanpur ke carton #4 me ghus gaya tha). Isliye pack tabhi
    // jab koi ek stall drive chuni ho.
    const blockIfNoDrive = () => {
        if (!driveOn) {
            showToast("Pick your stall drive above first — packing under All campuses mixes cartons across drives.", "warn");
            return true;
        }
        return false;
    };

    /* ── Carton mode — packing se alag ─────────────────────────────────────
       Yahan koi cheez apne aap nahi hoti: aap carton number chunte ho, packed
       list me se invoice tick karte ho, "Daalo" dabate ho, phir "Seal". */
    const [activeCartonNo, setActiveCartonNo] = useState(null);
    const [cartonPick, setCartonPick] = useState(() => new Set());
    const [cartonSearch, setCartonSearch] = useState("");
    const [newCartonNo, setNewCartonNo] = useState("");
    const [cartonTargetDraft, setCartonTargetDraft] = useState(DEFAULT_CARTON_TARGET);

    useEffect(() => { setCartonTargetDraft(cartonTarget); }, [cartonTarget]);
    // Drive badli → purani selection saaf (doosri drive ka maal mix na ho).
    useEffect(() => { setCartonPick(new Set()); setActiveCartonNo(null); setCartonSearch(""); }, [driveKey]);

    const activeCarton = useMemo(
        () => openCartons.find((c) => c.cartonNo === activeCartonNo) || null,
        [openCartons, activeCartonNo]
    );
    useEffect(() => {
        if (activeCartonNo != null && openCartons.some((c) => c.cartonNo === activeCartonNo)) return;
        setActiveCartonNo(openCartons.length ? openCartons[openCartons.length - 1].cartonNo : null);
    }, [openCartons, activeCartonNo]);

    const filteredLoose = useMemo(() => {
        const q = cartonSearch.trim();
        if (!q) return looseRows;
        const ql = q.toLowerCase();
        return looseRows.filter((r) => matchesInvoiceQuery(r.invoiceNo, q)
            || String(r.customerName || "").toLowerCase().includes(ql));
    }, [looseRows, cartonSearch]);

    const toggleCartonPick = (invoiceNo) => {
        if (isHeld(invoiceNo)) {
            showToast(`${invoiceNo} is on hold — check it, then clear the hold.`, "warn");
            return;
        }
        setCartonPick((prev) => {
            const n = new Set(prev);
            if (n.has(invoiceNo)) n.delete(invoiceNo); else n.add(invoiceNo);
            return n;
        });
    };
    /** Auto-select hold wali invoice kabhi na uthaye. */
    const selectableLoose = useMemo(
        () => filteredLoose.filter((r) => !isHeld(r.invoiceNo)),
        [filteredLoose, isHeld]
    );
    const pickFirstN = (n) =>
        setCartonPick(new Set(selectableLoose.slice(0, Math.max(0, n)).map((r) => r.invoiceNo)));

    /** Shak ho to invoice rok do — aur clear karne par phir se carton ke liye. */
    const toggleHold = async (row) => {
        const inv = row.invoiceNo;
        const on = isHeld(inv);
        try {
            await persistConfig({
                packHolds: {
                    [inv]: on
                        ? firebase.firestore.FieldValue.delete()
                        : { at: Date.now(), by: authEmail || "" },
                },
            });
            if (!on) {
                setCartonPick((prev) => {
                    if (!prev.has(inv)) return prev;
                    const n = new Set(prev);
                    n.delete(inv);
                    return n;
                });
            }
            showToast(
                on ? `${inv} cleared — ready for a carton` : `${inv} on hold — it will not go into a carton`,
                "ok"
            );
        } catch (e) {
            console.log("toggle hold", e);
            showToast("Could not change hold.", "err");
        }
    };

    const saveCartonTarget = async (raw) => {
        const n = Math.max(1, Math.min(999, Math.floor(num(raw)) || DEFAULT_CARTON_TARGET));
        setCartonTargetDraft(n);
        try { await persistDriveCarton({ cartonTarget: n }); }
        catch (e) { console.log("carton target", e); }
    };

    /** Sealed numbers ki current list (legacy drive ke liye derived se seed). */
    const sealedNumbersNow = () => (Array.isArray(driveCfg.cartonSealed)
        ? driveCfg.cartonSealed.map(num)
        : sealedCartons.map((c) => c.cartonNo));

    const createCarton = async () => {
        if (saving) return;
        if (blockIfNoDrive()) return;
        const n = Math.floor(num(newCartonNo)) || nextFreeCartonNo;
        if (n <= 0) { showToast("Carton number must be 1 or more.", "warn"); return; }
        const existing = cartons.find((c) => c.cartonNo === n);
        if (existing && existing.sealed) {
            showToast(`Carton #${n} is sealed — unseal it first.`, "warn");
            return;
        }
        setSaving(true);
        try {
            if (!existing) {
                const open = [...new Set([...(driveCfg.cartonOpen || []).map(num), n])];
                await persistDriveCarton({ cartonOpen: open });
            }
            setActiveCartonNo(n);
            setNewCartonNo("");
            showToast(existing ? `Carton #${n} selected` : `Carton #${n} opened`, "ok");
        } catch (e) {
            console.log("create carton", e);
            showToast("Could not create carton.", "err");
        } finally {
            setSaving(false);
        }
    };

    /** Chune hue packed invoices ko active carton me daalo. */
    const addPickedToCarton = async () => {
        if (saving || !activeCarton) return;
        if (blockIfNoDrive()) return;
        const no = activeCarton.cartonNo;
        const picked = looseRows.filter((r) => cartonPick.has(r.invoiceNo) && !isHeld(r.invoiceNo));
        const blocked = looseRows.filter((r) => cartonPick.has(r.invoiceNo) && isHeld(r.invoiceNo));
        if (blocked.length) {
            showToast(`${blocked.length} on-hold invoice(s) skipped — clear the hold first.`, "warn");
        }
        if (!picked.length) { showToast("Pick invoices below first.", "warn"); return; }
        const incomplete = picked.filter((r) => !r.complete);
        const ok = await askConfirm({
            title: `Carton #${no} — ${picked.length} invoice`,
            confirmLabel: `Add (${picked.length})`,
            tone: "primary",
            body: (
                <>
                    <p className="pack-cf-invlist">{picked.map((r) => r.invoiceNo).join(", ")}</p>
                    <p>Carton #{no} will then hold <b>{activeCarton.items.length + picked.length}</b> invoices.</p>
                    {incomplete.length > 0 && (
                        <p className="pack-cf-warn">⚠ {incomplete.length} invoice(s) are incomplete — some pieces still missing.</p>
                    )}
                </>
            ),
        });
        if (!ok) return;
        setSaving(true);
        try {
            const invSet = new Set(picked.map((r) => r.invoiceNo));
            const now = Date.now();
            const undoOps = [];
            const batch = db.batch();
            Object.values(assignments).forEach((a) => {
                if (!isBoxPacked(a) || !a.id) return;
                if (!driveBookingIds.has(a.bookingId)) return;
                if (a.packCarton != null) return; // pehle se kisi carton me
                const inv = a.invoiceNo || a.bookingId || "—";
                if (!invSet.has(inv)) return;
                undoOps.push({ col: COL_ASSIGN, id: a.id, prev: stripId({ ...a }) });
                batch.set(db.collection(COL_ASSIGN).doc(a.id), {
                    packCarton: no,
                    packCartonKind: "full",
                    packAt: now,
                    updatedAt: now,
                }, { merge: true });
            });
            if (!undoOps.length) { showToast("Nothing added — they may already be in a carton.", "warn"); return; }
            await batch.commit();
            logAction(`Carton #${no} · ${picked.length} invoices added (${undoOps.length} pcs)`, undoOps);
            setCartonPick(new Set());
            showToast(`Carton #${no} → ${picked.length} invoices · total ${activeCarton.items.length + picked.length}`, "ok", { canUndo: true });
        } catch (e) {
            console.log("add to carton", e);
            showToast("Could not add to carton.", "err");
        } finally {
            setSaving(false);
        }
    };

    /** Baad me aaye piece ko usi carton me jod do jisme baaki maal hai. */
    const attachLooseToCarton = async (row) => {
        if (saving || !row || row.carton == null) return;
        if (blockIfNoDrive()) return;
        const no = row.carton;
        const sealed = cartons.find((c) => c.cartonNo === no)?.sealed;
        const ok = await askConfirm({
            title: `Add the late piece to carton #${no}?`,
            confirmLabel: `Add to #${no}`,
            tone: "primary",
            body: (
                <>
                    <p>
                        <b>{row.invoiceNo}</b> — {row.cartonPieces} pcs are already in carton #{no}, and{" "}
                        <b>{row.looseTypes.join(", ")}</b> packed later is still loose.
                    </p>
                    <p className="pack-cf-warn">
                        {sealed
                            ? `Carton #${no} is sealed — open it, put the piece in, then confirm.`
                            : `Put the piece into carton #${no} first, then confirm.`}
                    </p>
                </>
            ),
        });
        if (!ok) return;
        setSaving(true);
        try {
            const now = Date.now();
            const undoOps = [];
            const batch = db.batch();
            Object.values(assignments).forEach((a) => {
                if (!isBoxPacked(a) || !a.id) return;
                if (!driveBookingIds.has(a.bookingId)) return;
                if (a.packCarton != null) return;
                if ((a.invoiceNo || a.bookingId || "—") !== row.invoiceNo) return;
                undoOps.push({ col: COL_ASSIGN, id: a.id, prev: stripId({ ...a }) });
                batch.set(db.collection(COL_ASSIGN).doc(a.id), {
                    packCarton: no,
                    packCartonKind: "full",
                    packAt: now,
                    updatedAt: now,
                }, { merge: true });
            });
            if (!undoOps.length) { showToast("Nothing left to add.", "warn"); return; }
            await batch.commit();
            logAction(`Late piece → carton #${no} · ${row.invoiceNo} (${undoOps.length} pcs)`, undoOps);
            showToast(`${row.invoiceNo} — ${undoOps.length} pcs added to carton #${no}`, "ok", { canUndo: true });
        } catch (e) {
            console.log("attach loose to carton", e);
            showToast("Could not add to carton.", "err");
        } finally {
            setSaving(false);
        }
    };

    /** Ek invoice carton se bahar — packing waisi hi rahegi. */
    const removeFromCarton = async (row) => {
        if (saving || !row) return;
        const ok = await askConfirm({
            title: `Remove ${row.invoiceNo} from carton?`,
            confirmLabel: "Remove",
            tone: "danger",
            body: <p>It goes back to the awaiting-carton list. Packing stays as it is — only the carton number is cleared.</p>,
        });
        if (!ok) return;
        setSaving(true);
        try {
            const undoOps = [];
            const batch = db.batch();
            Object.values(assignments).forEach((a) => {
                if (!isBoxPacked(a) || !a.id) return;
                if (!driveBookingIds.has(a.bookingId)) return;
                if (a.packCarton == null) return;
                if ((a.invoiceNo || a.bookingId || "—") !== row.invoiceNo) return;
                undoOps.push({ col: COL_ASSIGN, id: a.id, prev: stripId({ ...a }) });
                batch.set(db.collection(COL_ASSIGN).doc(a.id), {
                    packCarton: firebase.firestore.FieldValue.delete(),
                    packCartonKind: firebase.firestore.FieldValue.delete(),
                    updatedAt: Date.now(),
                }, { merge: true });
            });
            if (!undoOps.length) { showToast("Nothing changed.", "warn"); return; }
            await batch.commit();
            logAction(`Removed from carton · ${row.invoiceNo}`, undoOps);
            showToast(`${row.invoiceNo} removed from carton`, "ok", { canUndo: true });
        } catch (e) {
            console.log("remove from carton", e);
            showToast("Could not remove.", "err");
        } finally {
            setSaving(false);
        }
    };

    const sealCartonNo = async (no) => {
        if (saving) return;
        const c = cartons.find((x) => x.cartonNo === no);
        if (!c || !c.items.length) { showToast("An empty carton cannot be sealed.", "warn"); return; }
        const ok = await askConfirm({
            title: `Seal carton #${no}?`,
            confirmLabel: `Seal #${no}`,
            tone: "primary",
            body: <p><b>{c.items.length}</b> invoices · {c.pieces} pcs. The list locks once sealed — you can unseal it later if needed.</p>,
        });
        if (!ok) return;
        setSaving(true);
        try {
            const sealed = [...new Set([...sealedNumbersNow(), no])].sort((a, b) => a - b);
            const open = (driveCfg.cartonOpen || []).map(num).filter((x) => x !== no);
            await persistDriveCarton({ cartonSealed: sealed, cartonOpen: open });
            setActiveCartonNo(null);
            showToast(`Carton #${no} sealed · ${c.items.length} invoices`, "ok");
        } catch (e) {
            console.log("seal carton", e);
            showToast("Could not seal.", "err");
        } finally {
            setSaving(false);
        }
    };

    const unsealCartonNo = async (no) => {
        if (saving) return;
        const ok = await askConfirm({
            title: `Carton #${no} unseal?`,
            confirmLabel: "Unseal",
            body: <p>The carton reopens so you can add more invoices.</p>,
        });
        if (!ok) return;
        setSaving(true);
        try {
            const sealed = sealedNumbersNow().filter((x) => x !== no);
            const open = [...new Set([...(driveCfg.cartonOpen || []).map(num), no])].sort((a, b) => a - b);
            await persistDriveCarton({ cartonSealed: sealed, cartonOpen: open });
            setActiveCartonNo(no);
            showToast(`Carton #${no} reopened`, "ok");
        } catch (e) {
            console.log("unseal carton", e);
            showToast("Could not unseal.", "err");
        } finally {
            setSaving(false);
        }
    };

    /** Adhoora order — jo aa gaya wo pack. Carton baad me Carton mode se. */
    const packIncomplete = async (row) => {
        if (!row || saving) return;
        if (blockIfNoDrive()) return;
        if (!row.toPack.length) return;
        const ok = await askConfirm({
            title: `Partial pack — ${row.invoiceNo}`,
            confirmLabel: `Pack ${row.toPack.length} pcs`,
            tone: "primary",
            body: (
                <>
                    <PackItemList label="Packing now" items={row.toPack} tone="ok" />
                    <PackItemList label="Still missing (keep aside)" items={row.missing} tone="miss" />
                    <p className="pack-cf-warn">Carton comes later — add it from Carton packing.</p>
                </>
            ),
        });
        if (!ok) return;
        setSaving(true);
        try {
            const now = Date.now();
            const missingLabels = row.missingLabels || [];
            const undoOps = [];
            const batch = db.batch();
            row.toPack.forEach((it) => {
                const prev = assignments[it.key];
                undoOps.push({ col: COL_ASSIGN, id: it.key, prev: prev ? stripId({ ...prev }) : null });
                const base = prev || {};
                batch.set(db.collection(COL_ASSIGN).doc(it.key), {
                    ...base,
                    bookingId: row.bookingId,
                    invoiceNo: row.invoiceNo,
                    itemId: it.itemId,
                    itemName: it.itemName,
                    itemType: it.itemType,
                    fabric: it.fabric,
                    shade: it.shade || "",
                    customerName: row.customerName,
                    campus: row.campus,
                    tailor: base.tailor || "",
                    stage: "box_packed",
                    packingAt: now,
                    packAt: now,
                    updatedAt: now,
                    partialMissing: missingLabels,
                }, { merge: true });
            });
            await batch.commit();
            logAction(`Partial pack · ${row.invoiceNo} (${row.toPack.length} pcs · missing ${missingLabels.join(", ") || "—"})`, undoOps);
            showToast(`Packed ${row.invoiceNo} · ${row.toPack.length} pcs — carton later`, "ok", { canUndo: true });
        } catch (e) {
            console.log("incomplete pack", e);
            showToast("Could not pack.", "err");
        } finally {
            setSaving(false);
        }
    };
    const markReceived = async () => {
        if (!selected || saving) return;
        const invNo = selected.invoiceNo;
        const targets = tickedItems.filter((it) => !isReceived(it.assignment));
        if (!targets.length) { showToast("Selected items are already received.", "warn"); return; }
        const skipped = selected.activeItems.filter((it) => !ticks.has(it.key));
        const ok = await askConfirm({
            title: `Mark received — ${invNo}`,
            confirmLabel: `Mark ${targets.length} received`,
            body: (
                <>
                    <PackItemList label="Marking as received" items={targets} tone="ok" />
                    {skipped.length > 0 && <PackItemList label="Staying pending" items={skipped} tone="wait" />}
                </>
            ),
        });
        if (!ok) return;
        setSaving(true);
        try {
            await writeItems(targets, "received", "Packing received");
            showToast(`${targets.length} item(s) received · ${invNo}`, "ok", { canUndo: true });
        } catch (e) {
            console.log("receive", e);
            showToast("Could not save. Please try again.", "err");
        } finally {
            setSaving(false);
        }
    };

    /** Type list se ek piece — same CampusAssignments.stage = received (tailor/orders sab sync). */
    const receivePieceByType = async (piece) => {
        if (!piece || saving) return;
        if (isReceived(piece.assignment)) {
            showToast("Already received.", "warn");
            return;
        }
        setSaving(true);
        try {
            const undoOps = [];
            const now = Date.now();
            const prev = assignments[piece.key];
            const prevSnap = prev ? stripId({ ...prev }) : null;
            undoOps.push({ col: COL_ASSIGN, id: piece.key, prev: prevSnap });
            const base = prev || {};
            await db.collection(COL_ASSIGN).doc(piece.key).set({
                ...base,
                bookingId: piece.bookingId,
                invoiceNo: piece.invoiceNo,
                itemId: piece.itemId,
                itemName: piece.itemName,
                itemType: piece.itemType,
                fabric: piece.fabric,
                shade: piece.shade || "",
                customerName: piece.customerName,
                campus: piece.campus,
                tailor: base.tailor || "",
                stage: "received",
                packingAt: now,
                updatedAt: now,
            }, { merge: true });
            logAction(`Type receive · ${piece.invoiceNo} · ${piece.itemName || GARMENT_LABEL[piece.itemType]}`, undoOps);

            // Kya is invoice ka pair ab complete hai?
            const b = bookings.find((x) => x.id === piece.bookingId);
            const deliveredIds = b?.delivered_item_ids || [];
            const siblings = (b?.active_items || []).filter((item) => !deliveredIds.includes(item.id));
            const allReady = siblings.length > 0 && siblings.every((item) => {
                const k = garmentKey(b.id, item.id);
                if (k === piece.key) return true;
                return isReceived(assignments[k]);
            });

            if (allReady) {
                showToast(`Received · Pair ready: ${piece.invoiceNo}`, "ok", { canUndo: true });
                selectInvoice(piece.bookingId);
            } else {
                showToast(`Received · ${piece.invoiceNo} · ${piece.itemName || GARMENT_LABEL[piece.itemType]}`, "ok", { canUndo: true });
            }
        } catch (e) {
            console.log("type receive", e);
            showToast("Could not save. Please try again.", "err");
        } finally {
            setSaving(false);
        }
    };

    const logMissingPartial = async (missingItems) => {
        if (!missingItems.length) return;
        const entry = {
            invoiceNo: selected.invoiceNo,
            customerName: selected.customerName,
            bookingId: selected.bookingId,
            items: missingItems.map((it) => ({
                itemId: it.itemId,
                itemName: it.itemName || GARMENT_LABEL[it.itemType],
                itemType: it.itemType,
            })),
            at: Date.now(),
        };
        const newLog = [entry, ...missingLog].slice(0, 80);
        await persistConfig({ missingLog: newLog });
    };

    const packPartial = async () => {
        if (!selected || saving || !canPartialPack) return;
        if (blockIfNoDrive()) return;
        const invNo = selected.invoiceNo;
        const toPack = [...tickedItems];
        const stillMissing = selected.activeItems.filter((it) => !ticks.has(it.key));
        const ok = await askConfirm({
            title: `Pack selected — ${invNo}`,
            confirmLabel: `Pack ${toPack.length} now`,
            tone: "primary",
            body: (
                <>
                    <PackItemList label="Packing now" items={toPack} tone="ok" />
                    <PackItemList label="Still missing (stays in list)" items={stillMissing} tone="miss" />
                    <p className="pack-cf-warn">Carton comes later — add it from Carton packing.</p>
                </>
            ),
        });
        if (!ok) return;
        setSaving(true);
        try {
            const missingLabels = stillMissing.map((it) => it.itemName || GARMENT_LABEL[it.itemType] || it.itemType);
            await writeItems(toPack, "box_packed", "Partial pack", { missingLabels });
            await logMissingPartial(stillMissing);
            setTicks(new Set(stillMissing.map((it) => it.key)));
            showToast(`Packed ${toPack.length} · ${stillMissing.length} still missing — ${invNo}`, "ok", { canUndo: true });
        } catch (e) {
            console.log("partial pack", e);
            showToast("Could not save. Please try again.", "err");
        } finally {
            setSaving(false);
        }
    };

    const packInvoice = async () => {
        if (!selected || saving || !allActiveTicked) return;
        if (blockIfNoDrive()) return;
        const invNo = selected.invoiceNo;
        const packedItems = [...selected.activeItems];
        const notReceived = packedItems.filter((it) => !isReceived(it.assignment));
        const ok = await askConfirm({
            title: `Pack full invoice — ${invNo}`,
            confirmLabel: "Pack",
            tone: "primary",
            body: (
                <>
                    <PackItemList label={`Packing all ${packedItems.length} items`} items={packedItems} tone="ok" />
                    {notReceived.length > 0 && (
                        <p className="pack-cf-warn">⚠ {notReceived.length} item(s) not marked received yet — they will be marked received too.</p>
                    )}
                    <p className="pack-cf-note">Carton comes later — pack everything first, then fill cartons from Carton packing.</p>
                </>
            ),
        });
        if (!ok) return;
        setSaving(true);
        try {
            await writeItems(packedItems, "box_packed", "Box packed", { promoteReady: true });
            showToast(`Packed ${invNo} · ${packedItems.length} pcs — carton pending`, "ok", { canUndo: true });
            resetForNextPack();
        } catch (e) {
            console.log("pack", e);
            showToast("Could not pack. Please try again.", "err");
        } finally {
            setSaving(false);
        }
    };

    const todayPacked = useMemo(() => {
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        return packedRows.filter((r) => r.at >= start.getTime()).length;
    }, [packedRows]);

    /** Progress: partial + full / total (ek hi meter). */
    const overallPackProgress = useMemo(() => {
        let total = 0;
        let full = 0;
        let partial = 0;
        driveBookings.forEach((b) => {
            if (isPackingExcluded(b)) return;
            const deliveredIds = b.delivered_item_ids || [];
            const items = (b.active_items || []).filter((item) => !deliveredIds.includes(item.id));
            if (!items.length) return;
            total += 1;
            if (isBookingFullyPacked(b, assignments)) {
                full += 1;
            } else if (isBookingPartlyPacked(b, assignments)) {
                partial += 1;
            }
        });
        const done = partial + full;
        return {
            partial,
            full,
            done,
            total,
            pct: total > 0 ? Math.min(100, (done / total) * 100) : 0,
        };
    }, [driveBookings, assignments]);

    /** Coat 23 / Pant 18 style — received count + orders jahan kuch aaya kuch nahi. */
    /* ── Full detail: har missing piece, aur wo hai kahan ──────────────────
       Packing ke waqt sabse bada sawaal — "kaun sa piece nahi aaya, kiske
       paas hai, ya abhi kisi ko diya hi nahi". Wahi data jo Campus Orders ke
       Distribution tab me hai (CampusAssignments.tailor + stage), yahan
       packing ke andar. */
    const pendingPieceRows = useMemo(() => {
        const out = [];
        driveBookings.forEach((b) => {
            if (isPackingExcluded(b)) return;
            const deliveredIds = b.delivered_item_ids || [];
            (b.active_items || []).forEach((item) => {
                if (deliveredIds.includes(item.id)) return;
                if (!countsInDetail(item.type)) return; // tie alag — report se bahar
                const key = garmentKey(b.id, item.id);
                const a = assignments[key] || {};
                if (itemArrived(a)) return; // aa chuka — missing nahi
                const tailor = String(a.tailor || "").trim();
                const stage = normalizeAssignmentStage(a.stage);
                const fabric = String(b[`${item.id}_fabric`] || "").trim();
                const shade = String(b[`${item.id}_shade`] || "").trim();
                out.push({
                    key,
                    bookingId: b.id,
                    invoiceNo: b.invoice_no || "—",
                    customerName: b.customer_name || "—",
                    campus: b.campus || "—",
                    itemType: item.type,
                    type: GARMENT_LABEL[item.type] || item.type,
                    label: item.name || GARMENT_LABEL[item.type] || item.type,
                    fabric,
                    shade,
                    fabricLabel: [fabric, shade].filter(Boolean).join(" ") || NO_FABRIC,
                    tailor,
                    stage,
                    where: pendingWhereLabel(stage, tailor),
                    notIssued: !tailor,
                });
            });
        });
        return out;
    }, [driveBookings, assignments]);

    const pendingDetail = useMemo(() => {
        const q = missingSearch.trim();
        const ql = q.toLowerCase();
        const searched = q
            ? pendingPieceRows.filter((p) => matchesInvoiceQuery(p.invoiceNo, q)
                || String(p.customerName || "").toLowerCase().includes(ql)
                || String(p.tailor || "").toLowerCase().includes(ql)
                || String(p.type || "").toLowerCase().includes(ql)
                || String(p.fabricLabel || "").toLowerCase().includes(ql))
            : pendingPieceRows;

        const typeOrder = ["Coat", "Pant", "Shirt", "Tie", "Vest Coat"];

        // Filter chips ke counts search ke baad, type filter se pehle —
        // taaki chip dabane par baaki chips ke number na badlein.
        const tally = new Map();
        searched.forEach((p) => tally.set(p.type, (tally.get(p.type) || 0) + 1));
        const typeFilters = [...tally.entries()]
            .map(([type, count]) => ({ type, count }))
            .sort((a, b) => {
                const ai = typeOrder.indexOf(a.type), bi = typeOrder.indexOf(b.type);
                if (ai !== -1 && bi !== -1) return ai - bi;
                if (ai !== -1) return -1;
                if (bi !== -1) return 1;
                return a.type.localeCompare(b.type);
            });

        const pieces = detailType ? searched.filter((p) => p.type === detailType) : searched;
        const typeMap = new Map();
        const tailorMap = new Map();
        const fabricMap = new Map();
        pieces.forEach((p) => {
            // Fabric + shade ek hi thaan hai — "Turkish 312 ke 27 shirt".
            if (!fabricMap.has(p.fabricLabel)) {
                fabricMap.set(p.fabricLabel, {
                    label: p.fabricLabel, count: 0, pieces: [], types: new Map(),
                    tailors: new Map(),
                    unknown: p.fabricLabel === NO_FABRIC,
                });
            }
            const fg = fabricMap.get(p.fabricLabel);
            fg.count += 1;
            fg.pieces.push(p);
            fg.types.set(p.type, (fg.types.get(p.type) || 0) + 1);
            const who = p.tailor || NOT_ISSUED;
            fg.tailors.set(who, (fg.tailors.get(who) || 0) + 1);

            if (!typeMap.has(p.type)) typeMap.set(p.type, { type: p.type, count: 0, withTailor: 0, notIssued: 0, pieces: [] });
            const t = typeMap.get(p.type);
            t.count += 1;
            t.pieces.push(p);
            if (p.notIssued) t.notIssued += 1; else t.withTailor += 1;

            if (p.notIssued) return;
            if (!tailorMap.has(p.tailor)) tailorMap.set(p.tailor, { tailor: p.tailor, count: 0, pieces: [], types: new Map(), stages: new Map() });
            const g = tailorMap.get(p.tailor);
            g.count += 1;
            g.pieces.push(p);
            g.types.set(p.type, (g.types.get(p.type) || 0) + 1);
            g.stages.set(p.where, (g.stages.get(p.where) || 0) + 1);
        });

        const byType = [...typeMap.values()].sort((a, b) => {
            const ai = typeOrder.indexOf(a.type), bi = typeOrder.indexOf(b.type);
            if (ai !== -1 && bi !== -1) return ai - bi;
            if (ai !== -1) return -1;
            if (bi !== -1) return 1;
            return a.type.localeCompare(b.type);
        });
        byType.forEach((t) => t.pieces.sort(sortInvoicesAlpha));

        const byTailor = [...tailorMap.values()]
            .map((g) => ({
                ...g,
                pieces: [...g.pieces].sort(sortInvoicesAlpha),
                types: [...g.types.entries()].sort((a, b) => b[1] - a[1]),
                stages: [...g.stages.entries()].sort((a, b) => b[1] - a[1]),
            }))
            .sort((a, b) => b.count - a.count || a.tailor.localeCompare(b.tailor));

        const byFabric = [...fabricMap.values()]
            .map((g) => ({
                ...g,
                pieces: [...g.pieces].sort(sortInvoicesAlpha),
                types: [...g.types.entries()].sort((a, b) => b[1] - a[1]),
                tailors: [...g.tailors.entries()].sort((a, b) =>
                    (a[0] === NOT_ISSUED ? 1 : 0) - (b[0] === NOT_ISSUED ? 1 : 0) || b[1] - a[1]),
            }))
            .sort((a, b) => (a.unknown ? 1 : 0) - (b.unknown ? 1 : 0)
                || b.count - a.count
                || a.label.localeCompare(b.label, undefined, { numeric: true }));

        const notIssuedPieces = pieces.filter((p) => p.notIssued).sort(sortInvoicesAlpha);
        const invoices = new Set(pieces.map((p) => p.invoiceNo));

        return {
            pieces,
            total: pieces.length,
            withTailor: pieces.length - notIssuedPieces.length,
            notIssued: notIssuedPieces.length,
            orders: invoices.size,
            searchedTotal: searched.length,
            typeFilters,
            byType,
            byFabric,
            byTailor,
            notIssuedPieces,
        };
    }, [pendingPieceRows, missingSearch, detailType]);

    const missingReport = useMemo(() => {
        const typeTotal = {};
        const typeReceived = {};
        const typePacked = {};
        const orderRows = [];

        driveBookings.filter((b) => !isPackingExcluded(b)).forEach((b) => {
            const items = bookingItemsForReport(b, assignments)
                .filter((it) => countsInDetail(it.itemType)); // tie alag
            if (!items.length) return;
            if (items.every((it) => it.packed)) return;

            items.forEach((it) => {
                const label = GARMENT_LABEL[it.itemType] || it.itemName || it.itemType;
                typeTotal[label] = (typeTotal[label] || 0) + 1;
                if (it.arrived) typeReceived[label] = (typeReceived[label] || 0) + 1;
                if (it.packed) typePacked[label] = (typePacked[label] || 0) + 1;
            });

            const arrived = items.filter((it) => it.arrived);
            const pending = items.filter((it) => !it.arrived);
            const pieceLabel = (it) => it.itemName || GARMENT_LABEL[it.itemType] || it.itemType;
            const pieceType = (it) => GARMENT_LABEL[it.itemType] || it.itemType;
            if (arrived.length > 0 && pending.length > 0) {
                orderRows.push({
                    bookingId: b.id,
                    invoiceNo: b.invoice_no || "—",
                    customerName: b.customer_name || "—",
                    campus: b.campus || "—",
                    arrived: arrived.map(pieceLabel),
                    pending: pending.map(pieceLabel),
                    pendingItems: pending.map((it) => ({ label: pieceLabel(it), type: pieceType(it) })),
                });
            }
        });

        const typeOrder = ["Coat", "Pant", "Shirt", "Tie", "Vest Coat"];
        const pendingByType = {};
        orderRows.forEach((row) => {
            (row.pendingItems || []).forEach((p) => {
                if (!pendingByType[p.type]) pendingByType[p.type] = { count: 0, invoices: new Map() };
                pendingByType[p.type].count += 1;
                if (!pendingByType[p.type].invoices.has(row.invoiceNo)) {
                    pendingByType[p.type].invoices.set(row.invoiceNo, {
                        invoiceNo: row.invoiceNo,
                        customerName: row.customerName,
                        bookingId: row.bookingId,
                        pieces: [],
                    });
                }
                pendingByType[p.type].invoices.get(row.invoiceNo).pieces.push(p.label);
            });
        });

        const typeBreakdown = [
            ...typeOrder.filter((t) => pendingByType[t]?.count > 0).map((type) => ({
                type,
                count: pendingByType[type].count,
                invoices: [...pendingByType[type].invoices.values()].sort((a, b) =>
                    String(a.invoiceNo).localeCompare(String(b.invoiceNo), undefined, { numeric: true })
                ),
            })),
            ...Object.keys(pendingByType)
                .filter((t) => !typeOrder.includes(t) && pendingByType[t].count > 0)
                .sort()
                .map((type) => ({
                    type,
                    count: pendingByType[type].count,
                    invoices: [...pendingByType[type].invoices.values()].sort((a, b) =>
                        String(a.invoiceNo).localeCompare(String(b.invoiceNo), undefined, { numeric: true })
                    ),
                })),
        ];

        const typeOrderLegacy = typeOrder;
        const typeSummary = Object.keys(typeTotal)
            .map((type) => ({
                type,
                total: typeTotal[type],
                received: typeReceived[type] || 0,
                packed: typePacked[type] || 0,
                pending: typeTotal[type] - (typeReceived[type] || 0),
            }))
            .sort((a, b) => {
                const ai = typeOrderLegacy.indexOf(a.type);
                const bi = typeOrderLegacy.indexOf(b.type);
                if (ai !== -1 && bi !== -1) return ai - bi;
                if (ai !== -1) return -1;
                if (bi !== -1) return 1;
                return a.type.localeCompare(b.type);
            });

        const maxReceived = typeSummary.reduce((m, s) => Math.max(m, s.received), 0);
        typeSummary.forEach((s) => {
            s.shortBy = maxReceived > s.received ? maxReceived - s.received : 0;
        });

        orderRows.sort((a, b) => String(a.invoiceNo).localeCompare(String(b.invoiceNo), undefined, { numeric: true }));

        const pendingPieces = orderRows.reduce((n, r) => n + r.pending.length, 0);
        return { typeSummary, typeBreakdown, orderRows, pendingPieces, maxReceived };
    }, [driveBookings, assignments]);

    const openMissingOrder = (bookingId) => {
        selectInvoice(bookingId);
        setMissingSearch("");
        setMissingModal(false);
    };

    const filteredMissingReport = useMemo(() => {
        const q = missingSearch.trim();
        if (!q) return missingReport;
        const matchRow = (row) => matchesInvoiceQuery(row.invoiceNo, q)
            || String(row.customerName || "").toLowerCase().includes(q.toLowerCase());
        const orderRows = missingReport.orderRows.filter(matchRow);
        const typeBreakdown = missingReport.typeBreakdown
            .map((block) => {
                const invoices = block.invoices.filter((inv) => matchesInvoiceQuery(inv.invoiceNo, q)
                    || String(inv.customerName || "").toLowerCase().includes(q.toLowerCase()));
                return { ...block, count: invoices.reduce((n, inv) => n + inv.pieces.length, 0), invoices };
            })
            .filter((block) => block.invoices.length > 0);
        const pendingPieces = orderRows.reduce((n, r) => n + r.pending.length, 0);
        return { ...missingReport, orderRows, typeBreakdown, pendingPieces };
    }, [missingReport, missingSearch]);

    const openMissingModal = () => {
        setMissingSearch("");
        setDetailType("");
        setMissingModal(true);
    };

    const closeMissingModal = () => {
        setMissingSearch("");
        setMissingModal(false);
    };

    const pendingSingleCount = useMemo(
        () => RECEIVE_TYPES.reduce((n, t) => n + (receiveTypeCounts[t] || 0), 0),
        [receiveTypeCounts]
    );

    return (
        <div className="pack-page">
            <div className="campus-drive-bar pack-drive-bar">
                <span className="campus-drive-label">Stall drive</span>
                <button
                    type="button"
                    className={`campus-drive-chip ${!driveOn ? "is-on" : ""}`}
                    onClick={() => setDrive({ campus: "", from: "", to: "" })}
                >
                    All campuses<em>{bookings.length}</em>
                </button>
                {drives.map((d) => {
                    const on = drive.campus === d.campus && drive.from === d.from && drive.to === d.to;
                    return (
                        <button
                            type="button"
                            key={`${d.campus}|${d.from}|${d.to}`}
                            className={`campus-drive-chip ${on ? "is-on" : ""} ${d.pending === 0 ? "is-done" : ""}`}
                            onClick={() => setDrive(on ? { campus: "", from: "", to: "" } : { campus: d.campus, from: d.from, to: d.to })}
                            title={`${d.campus} · ${d.from} to ${d.to} · ${d.pending} left to pack, ${d.count} orders total`}
                        >
                            {d.campus} · {fmtDriveRange(d.from, d.to)}
                            <em>{d.pending === 0 ? "✓" : d.pending}</em>
                        </button>
                    );
                })}
                {driveOn && !driveInChips && (
                    <button
                        type="button"
                        className="campus-drive-chip is-on"
                        onClick={() => setDrive({ campus: "", from: "", to: "" })}
                        title="This selection matches no drive — click to clear it"
                    >
                        {drive.campus || "All"}{drive.from ? ` · ${fmtDriveRange(drive.from, drive.to)}` : ""}
                        <em>{driveBookings.length}</em>
                    </button>
                )}
                <button
                    type="button"
                    className={`campus-drive-custom-btn ${driveCustom ? "is-on" : ""}`}
                    onClick={() => setDriveCustom((o) => !o)}
                >
                    Custom ▾
                </button>
                {driveOn ? (
                    <span className={`campus-drive-count ${driveBookings.length === 0 ? "pack-drive-warn" : ""}`}>
                        {driveBookings.length === 0
                            ? "⚠ No packable orders in this drive — pick another one"
                            : <>Packing only <strong>{driveBookings.length}</strong> of {bookings.length} orders — other drives hidden</>}
                    </span>
                ) : (
                    <span className="campus-drive-count pack-drive-warn">
                        ⚠ All campuses mixed — select a drive to avoid packing the wrong campus
                    </span>
                )}
            </div>

            {driveCustom && (
                <div className="campus-drive-custom">
                    <select value={drive.campus} onChange={(e) => setDrive((d) => ({ ...d, campus: e.target.value }))}>
                        <option value="">All campuses</option>
                        {[...new Set(allBookings.map((b) => b.campus).filter(Boolean))].sort().map((c) => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                    </select>
                    <label>From<input type="date" value={drive.from} onChange={(e) => setDrive((d) => ({ ...d, from: e.target.value }))} /></label>
                    <label>To<input type="date" value={drive.to} onChange={(e) => setDrive((d) => ({ ...d, to: e.target.value }))} /></label>
                    {driveOn && (
                        <button type="button" className="pack-filter-clear" onClick={() => setDrive({ campus: "", from: "", to: "" })}>Clear</button>
                    )}
                </div>
            )}

            {loadError && (
                <div className="pack-load-error">
                    <strong>
                        {loadError.denied
                            ? "This login cannot read business data"
                            : "Could not load data"}
                    </strong>
                    <span>
                        {loadError.denied
                            ? "Firestore rules let only the dedicated admin accounts (…@maviinci.in) read bookings. Firestore refused the account signed in on this browser — that is why the packing list is empty. No orders are lost."
                            : "Bookings did not come back from the server. Check the network and try again."}
                    </span>
                    <span className="pack-load-error-who">
                        Signed in: <b>{authEmail || "— none —"}</b>
                        <i>{loadError.detail}</i>
                    </span>
                    <button type="button" className="pack-btn pack-btn--pri" onClick={loadBookings} disabled={loading}>
                        {loading ? "Retrying…" : "Try again"}
                    </button>
                </div>
            )}

            {!loadError && driveOn && !loading && driveBookings.length === 0 && (
                <div className="pack-drive-empty">
                    <strong>Nothing left to pack in this drive</strong>
                    <span>
                        {drive.campus || "All campuses"}
                        {drive.from ? ` · ${fmtDriveRange(drive.from, drive.to)}` : ""} — every order here is
                        already delivered or cancelled, so the list is empty. Pick another drive above.
                    </span>
                    <button
                        type="button"
                        className="pack-btn pack-btn--pri"
                        onClick={() => setDrive({ campus: "", from: "", to: "" })}
                    >
                        Show all campuses ({bookings.length} orders)
                    </button>
                </div>
            )}

            {!packMode && (
                <div className="pack-mode-gate">
                    <div className="pack-mode-gate-inner">
                        <span className="pack-mode-gate-kicker">Packing</span>
                        <h1 className="pack-mode-gate-title">What do you want to do?</h1>
                        <p className="pack-mode-gate-sub">Pick a mode — the screen opens to match.</p>
                        <div className="pack-mode-gate-cards pack-mode-gate-cards--4">
                            <button
                                type="button"
                                className="pack-mode-card pack-mode-card--pair"
                                onClick={() => setPackMode("pair")}
                            >
                                <strong>Pair packing</strong>
                                <span>Select a whole invoice and pack it</span>
                                <em>{readyList.length} pairs ready</em>
                            </button>
                            <button
                                type="button"
                                className="pack-mode-card pack-mode-card--single"
                                onClick={() => setPackMode("single")}
                            >
                                <strong>Single receive</strong>
                                <span>Pant / Shirt / Coat list se click-click received</span>
                                <em>{pendingSingleCount} pieces pending · system pair batayega</em>
                            </button>
                            <button
                                type="button"
                                className="pack-mode-card pack-mode-card--partial"
                                onClick={() => setPackMode("partial")}
                            >
                                <strong>Partial pack</strong>
                                <span>Coat required — pack even if shirt/pant is missing</span>
                                <em>{partialEligibleList.length} eligible</em>
                            </button>
                            <button
                                type="button"
                                className="pack-mode-card pack-mode-card--carton"
                                onClick={() => setPackMode("carton")}
                            >
                                <strong>Carton packing</strong>
                                <span>Fill a carton from packed stock, then seal it</span>
                                <em>{looseRows.length} awaiting carton · {openCartons.length} open</em>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {packMode && (
                <>
                    <div className="pack-mode-bar">
                        <div className="pack-mode-bar-left">
                            <span className="pack-mode-bar-label">
                                {packMode === "pair" ? "Pair packing" : packMode === "single" ? "Single receive" : packMode === "carton" ? "Carton packing" : "Partial pack"}
                            </span>
                            <button type="button" className="pack-mode-switch" onClick={() => setPackMode(null)}>
                                Change mode
                            </button>
                        </div>
                        <div className="pack-mode-bar-actions">
                            <button type="button" className="pack-refresh" onClick={loadBookings} disabled={loading} aria-label="Refresh">
                                {loading ? "…" : "↻"}
                            </button>
                            <button
                                type="button"
                                className="pack-undo-btn"
                                onClick={undoLast}
                                disabled={saving}
                                title="Last packing action undo (code chahiye)"
                            >
                                ↩ Undo
                            </button>
                            <button type="button" className="pack-missing-open" onClick={openMissingModal}>
                                Full detail
                                {pendingPieceRows.length > 0 && (
                                    <span className="pack-missing-open-badge">{pendingPieceRows.length}</span>
                                )}
                            </button>
                            <button type="button" className="pack-missing-open pack-cartons-open" onClick={() => setPackMode("carton")}>
                                Cartons
                                {looseRows.length > 0 && (
                                    <span className="pack-missing-open-badge">{looseRows.length}</span>
                                )}
                            </button>
                        </div>
                    </div>

                    {packMode === "pair" && (
                        <>
            <div className="pack-sticky-top">
                <div className="pack-ready-banner">
                    <div className="pack-ready-copy">
                        <span className="pack-ready-kicker">You can pack now</span>
                        <strong className="pack-ready-count">{readyList.length} order{readyList.length !== 1 ? "s" : ""} ready</strong>
                        <span className="pack-ready-sub">All items received — select below to pack</span>
                    </div>
                    <div
                        className="pack-overall-meter"
                        title={`Partial ${overallPackProgress.partial} + full ${overallPackProgress.full} = ${overallPackProgress.done} of ${overallPackProgress.total}`}
                    >
                        <span className="pack-carton-label">Partial + full</span>
                        <strong className="pack-carton-val">
                            <em className="pack-prog-partial">{overallPackProgress.partial}</em>
                            <span className="pack-prog-plus"> + </span>
                            <em className="pack-prog-full">{overallPackProgress.full}</em>
                            <span className="pack-prog-slash"> / {overallPackProgress.total}</span>
                        </strong>
                        <div className="pack-carton-bar">
                            <div className="pack-carton-fill pack-carton-fill--overall" style={{ width: `${overallPackProgress.pct}%` }} />
                        </div>
                    </div>
                    <div className="pack-carton-meter">
                        <span className="pack-carton-label">Awaiting carton</span>
                        <strong className="pack-carton-val">{looseRows.length}</strong>
                        <div className="pack-carton-bar">
                            <div className="pack-carton-fill" style={{ width: `${cartonProgressPct}%` }} />
                        </div>
                    </div>
                </div>

                {readyList.length > 0 && (
                    <div className="pack-ready-chips">
                        {readyList.map((inv) => (
                            <button
                                key={inv.bookingId}
                                type="button"
                                className={`pack-chip ${selectedId === inv.bookingId ? "is-on" : ""}`}
                                onClick={() => selectInvoice(inv.bookingId)}
                            >
                                <InvNo value={inv.invoiceNo} />
                                <em>{inv.receivedCount}/{inv.activeTotal}</em>
                            </button>
                        ))}
                    </div>
                )}

                <div className="pack-toolbar">
                    <input
                        ref={searchInputRef}
                        type="search"
                        className={`pack-search ${searchFresh ? "is-fresh" : ""}`}
                        placeholder={searchFresh ? "Next invoice — type the number" : "Search — 1 = only IITKAN1"}
                        value={search}
                        onChange={onPackSearchChange}
                        autoComplete="off"
                        enterKeyHint="search"
                    />
                    {searchFresh && (
                        <p className="pack-search-fresh-hint">Packed ✓ — search the next number here</p>
                    )}
                    <select
                        className="pack-invoice-select"
                        value={selectedId}
                        onChange={(e) => selectInvoice(e.target.value)}
                    >
                        <option value="">— Select invoice —</option>
                        {filtered.map((inv) => (
                            <option key={inv.bookingId} value={inv.bookingId}>
                                {inv.invoiceNo} · {inv.customerName} · {inv.campus} ({inv.receivedCount}/{inv.activeTotal})
                            </option>
                        ))}
                    </select>
                    <select className="pack-fabric" value={fabricFilter} onChange={(e) => setFabricFilter(e.target.value)}>
                        <option value="">All fabrics</option>
                        {fabricOptions.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                    <div className="pack-toolbar-actions">
                        <button type="button" className="pack-refresh" onClick={loadBookings} disabled={loading} aria-label="Refresh">
                            {loading ? "…" : "↻"}
                        </button>
                        <button
                            type="button"
                            className="pack-missing-open"
                            onClick={openMissingModal}
                        >
                            Full detail
                            {pendingPieceRows.length > 0 && (
                                <span className="pack-missing-open-badge">{pendingPieceRows.length}</span>
                            )}
                        </button>
                        <button
                            type="button"
                            className="pack-missing-open pack-cartons-open"
                            onClick={() => setPackMode("carton")}
                        >
                            Cartons
                            {looseRows.length > 0 && (
                                <span className="pack-missing-open-badge">{looseRows.length}</span>
                            )}
                        </button>
                    </div>
                </div>

                <div className="pack-mini-stats">
                    <span><b>{readyList.length}</b> ready</span>
                    <span><b>{progressList.length}</b> in progress</span>
                    <span><b>{filtered.length}</b> shown{search.trim() || fabricFilter ? " (filtered)" : ""}</span>
                    <span><b>{todayPacked}</b> packed today</span>
                    {(search.trim() || fabricFilter) && (
                        <button
                            type="button"
                            className="pack-filter-clear"
                            onClick={() => { setSearch(""); setFabricFilter(""); }}
                        >
                            Clear filters
                        </button>
                    )}
                </div>
            </div>

            {progressList.length > 0 && (
                <details className="pack-progress-fold">
                    <summary>In progress — {progressList.length} partial order{progressList.length !== 1 ? "s" : ""}</summary>
                    <div className="pack-progress-list">
                        {progressList.map((inv) => (
                            <button
                                key={inv.bookingId}
                                type="button"
                                className={`pack-progress-row ${selectedId === inv.bookingId ? "is-on" : ""}`}
                                onClick={() => selectInvoice(inv.bookingId)}
                            >
                                <strong><InvNo value={inv.invoiceNo} /></strong>
                                <span>{inv.customerName}</span>
                                <span className="pack-progress-badge">{inv.receivedCount}/{inv.activeTotal} received</span>
                            </button>
                        ))}
                    </div>
                </details>
            )}

            {!selected ? (
                <div className="pack-empty">
                    {loading
                        ? "Loading orders…"
                        : search.trim() || fabricFilter
                            ? `No orders match${search.trim() ? ` “${search.trim()}”` : ""}${fabricFilter ? ` · fabric ${fabricFilter}` : ""}.`
                            : filtered.length
                                ? "Select an invoice above."
                                : "No orders in packing queue."}
                </div>
            ) : (
                <div className="pack-work">
                    <div className="pack-order-head">
                        <div>
                            <span className="pack-order-kicker">Active order</span>
                            <h2 className="pack-order-inv"><InvNo value={selected.invoiceNo} /></h2>
                            <p className="pack-order-meta">{selected.customerName} · {selected.campus}</p>
                        </div>
                        <span className={`pack-order-badge ${selected.status === "ready" ? "is-ready" : ""}`}>
                            {selected.receivedCount}/{selected.activeTotal} received
                            {selected.status === "ready" ? " · ready to pack" : ""}
                        </span>
                    </div>

                    <div className="pack-items">
                        {selected.activeItems.map((it) => {
                            const rec = isReceived(it.assignment);
                            const on = ticks.has(it.key);
                            return (
                                <label
                                    key={it.key}
                                    className={`pack-item ${on ? "is-on" : ""} ${rec ? "is-rec" : ""}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={on}
                                        disabled={isBoxPacked(it.assignment)}
                                        onChange={() => toggleTick(it.key, it)}
                                    />
                                    <span className="pack-item-check" aria-hidden />
                                    <div className="pack-item-body">
                                        <strong>{it.itemName || GARMENT_LABEL[it.itemType]}</strong>
                                        <span className="pack-item-fab">
                                            {it.itemType === "tie" ? (
                                                <em className="pack-item-shade">
                                                    {swatchFor(it.fabric) && <i className="pack-swatch" style={{ background: swatchFor(it.fabric) }} />}
                                                    {it.fabric || "—"}
                                                </em>
                                            ) : (
                                                <>
                                                    {it.fabric || "—"}
                                                    {it.shade && (
                                                        <em className="pack-item-shade">
                                                            {swatchFor(it.shade) && <i className="pack-swatch" style={{ background: swatchFor(it.shade) }} />}
                                                            {it.shade}
                                                        </em>
                                                    )}
                                                </>
                                            )}
                                        </span>
                                    </div>
                                    <span className={`pack-item-tag ${rec ? "tag-ok" : "tag-wait"}`}>
                                        {rec ? "Received" : "Pending"}
                                    </span>
                                </label>
                            );
                        })}
                    </div>

                    {selectedMissing.length > 0 && (
                        <div className="pack-note-inline">
                            <strong>Missing from this pack</strong>
                            <span>{selectedMissing.map((it) => it.itemName || GARMENT_LABEL[it.itemType]).join(" · ")}</span>
                        </div>
                    )}

                    {!allActiveTicked && !selectedMissing.length && (
                        <p className="pack-hint">Untick whatever you are not packing — it gets noted here.</p>
                    )}
                </div>
            )}

            <div className="pack-dock">
                <button
                    type="button"
                    className="pack-btn pack-btn--sec"
                    disabled={!selected || saving || !canReceive}
                    onClick={markReceived}
                >
                    Received only{tickedItems.length ? ` (${tickedItems.filter((it) => !isReceived(it.assignment)).length})` : ""}
                </button>
                <button
                    type="button"
                    className="pack-btn pack-btn--partial"
                    disabled={!selected || saving || !canPartialPack}
                    onClick={packPartial}
                >
                    Pack selected ({tickedItems.length || 0})
                </button>
                <button
                    type="button"
                    className="pack-btn pack-btn--pri"
                    disabled={!selected || saving || !allActiveTicked || !canPack}
                    onClick={packInvoice}
                >
                    Pack full invoice
                </button>
                <span className="pack-dock-note">
                    {selected ? `${tickedItems.length}/${selected.activeItems.length} selected` : "—"}
                </span>
            </div>
                        </>
                    )}

                    {packMode === "single" && (
                        <>
                            <div className="pack-sticky-top pack-sticky-top--single">
                                <div className="pack-ready-banner">
                                    <div className="pack-ready-copy">
                                        <span className="pack-ready-kicker">Single receive</span>
                                        <strong className="pack-ready-count">
                                            {GARMENT_LABEL[receiveType] || receiveType} — {receiveTypePieces.length} pending
                                        </strong>
                                        <span className="pack-ready-sub">Click row → received everywhere (tailor / orders sync)</span>
                                    </div>
                                    <div
                                        className="pack-overall-meter"
                                        title={`Partial ${overallPackProgress.partial} + full ${overallPackProgress.full} = ${overallPackProgress.done} of ${overallPackProgress.total}`}
                                    >
                                        <span className="pack-carton-label">Partial + full</span>
                                        <strong className="pack-carton-val">
                                            <em className="pack-prog-partial">{overallPackProgress.partial}</em>
                                            <span className="pack-prog-plus"> + </span>
                                            <em className="pack-prog-full">{overallPackProgress.full}</em>
                                            <span className="pack-prog-slash"> / {overallPackProgress.total}</span>
                                        </strong>
                                        <div className="pack-carton-bar">
                                            <div className="pack-carton-fill pack-carton-fill--overall" style={{ width: `${overallPackProgress.pct}%` }} />
                                        </div>
                                    </div>
                                    <div className="pack-carton-meter">
                                        <span className="pack-carton-label">Awaiting carton</span>
                                        <strong className="pack-carton-val">{looseRows.length}</strong>
                                        <div className="pack-carton-bar">
                                            <div className="pack-carton-fill" style={{ width: `${cartonProgressPct}%` }} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <section className="pack-type-receive" aria-label="Receive by garment type">
                                <div className="pack-type-receive-head">
                                    <div>
                                        <span className="pack-type-receive-kicker">Receive by type</span>
                                        <strong className="pack-type-receive-title">Bachi list — click se received</strong>
                                    </div>
                                    {readyList.length > 0 && (
                                        <div className="pack-pair-ready" aria-live="polite">
                                            <span className="pack-pair-ready-label">Pairs ready ({readyList.length})</span>
                                            <div className="pack-pair-ready-chips">
                                                {readyList.map((inv) => (
                                                    <button
                                                        key={inv.bookingId}
                                                        type="button"
                                                        className={`pack-pair-chip ${selectedId === inv.bookingId ? "is-on" : ""}`}
                                                        onClick={() => selectInvoice(inv.bookingId)}
                                                        title={`${inv.customerName} — pack full invoice`}
                                                    >
                                                        <InvNo value={inv.invoiceNo} />
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="pack-type-tabs" role="tablist">
                                    {RECEIVE_TYPES.map((t) => (
                                        <button
                                            key={t}
                                            type="button"
                                            role="tab"
                                            aria-selected={receiveType === t}
                                            className={`pack-type-tab ${receiveType === t ? "is-on" : ""}`}
                                            onClick={() => setReceiveType(t)}
                                        >
                                            {GARMENT_LABEL[t] || t}
                                            <em>{receiveTypeCounts[t] || 0}</em>
                                        </button>
                                    ))}
                                </div>
                                {receiveTypePieces.length === 0 ? (
                                    <div className="pack-type-empty">
                                        No pending {GARMENT_LABEL[receiveType] || receiveType}s — sab received / packed.
                                    </div>
                                ) : (
                                    <div className="pack-type-list pack-type-list--tall">
                                        {receiveTypePieces.map((p) => (
                                            <button
                                                key={p.key}
                                                type="button"
                                                className="pack-type-row"
                                                disabled={saving}
                                                onClick={() => receivePieceByType(p)}
                                                title="Mark received (syncs to tailor / orders)"
                                            >
                                                <strong className="pack-type-row-inv"><InvNo value={p.invoiceNo} /></strong>
                                                <span className="pack-type-row-name">{p.customerName}</span>
                                                <span className="pack-type-row-fab">
                                                    {p.itemType === "tie" ? (p.fabric || "—") : (
                                                        <>
                                                            {p.fabric || "—"}
                                                            {p.shade ? ` · ${p.shade}` : ""}
                                                        </>
                                                    )}
                                                </span>
                                                <span className="pack-type-row-action">Received</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </section>

                            {selected && selected.status === "ready" && (
                                <div className="pack-work pack-work--single-ready">
                                    <div className="pack-order-head">
                                        <div>
                                            <span className="pack-order-kicker">Pair ready — pack to carton</span>
                                            <h2 className="pack-order-inv"><InvNo value={selected.invoiceNo} /></h2>
                                            <p className="pack-order-meta">{selected.customerName} · {selected.campus}</p>
                                        </div>
                                        <span className="pack-order-badge is-ready">
                                            {selected.receivedCount}/{selected.activeTotal} received · ready to pack
                                        </span>
                                    </div>
                                    <div className="pack-items">
                                        {selected.activeItems.map((it) => (
                                            <div key={it.key} className="pack-item is-on is-rec">
                                                <span className="pack-item-check" aria-hidden />
                                                <div className="pack-item-body">
                                                    <strong>{it.itemName || GARMENT_LABEL[it.itemType]}</strong>
                                                    <span className="pack-item-fab">{it.fabric || "—"}{it.shade ? ` · ${it.shade}` : ""}</span>
                                                </div>
                                                <span className="pack-item-tag tag-ok">Received</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="pack-dock">
                                <button
                                    type="button"
                                    className="pack-btn pack-btn--pri"
                                    disabled={!selected || selected.status !== "ready" || saving || !canPack}
                                    onClick={packInvoice}
                                >
                                    Pack full invoice{selected?.status === "ready" ? ` · ${selected.invoiceNo}` : ""}
                                </button>
                                <span className="pack-dock-note">
                                    {readyList.length
                                        ? `${readyList.length} pair${readyList.length !== 1 ? "s" : ""} ready — tap a chip to pack`
                                        : "Receive pieces — packing appears here once a pair completes"}
                                </span>
                            </div>
                        </>
                    )}

                    {packMode === "partial" && (
                        <>
                            <div className="pack-sticky-top">
                                <div className="pack-ready-banner">
                                    <div className="pack-ready-copy">
                                        <span className="pack-ready-kicker">Partial pack</span>
                                        <strong className="pack-ready-count">{partialEligibleList.length} eligible</strong>
                                        <span className="pack-ready-sub">Coat required — pack what has arrived, carton later</span>
                                    </div>
                                    <div className="pack-carton-meter">
                                        <span className="pack-carton-label">Awaiting carton</span>
                                        <strong className="pack-carton-val">{looseRows.length}</strong>
                                        <div className="pack-carton-bar">
                                            <div className="pack-carton-fill" style={{ width: `${cartonProgressPct}%` }} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <section className="pack-partial-section">
                                <h3 className="pack-partial-list-title">Eligible (coat covered + something missing)</h3>
                                {partialEligibleList.length === 0 ? (
                                    <div className="pack-type-empty">
                                        Nothing eligible — receive a coat from Single receive first, and the invoice must be incomplete.
                                    </div>
                                ) : (
                                    <div className="pack-partial-list">
                                        {partialEligibleList.map((row) => (
                                            <div className="pack-partial-row" key={row.bookingId}>
                                                <div className="pack-partial-row-main">
                                                    <strong><InvNo value={row.invoiceNo} /></strong>
                                                    <span>{row.customerName}</span>
                                                    <em className="pack-partial-have">
                                                        Pack: {row.toPack.map((it) => it.itemName || GARMENT_LABEL[it.itemType]).join(", ")}
                                                    </em>
                                                    <em className="pack-partial-miss">
                                                        Missing: {row.missingLabels.join(", ")}
                                                    </em>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="pack-btn pack-btn--pri pack-partial-pack-btn"
                                                    disabled={saving}
                                                    onClick={() => packIncomplete(row)}
                                                >
                                                    Pack
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </section>
                        </>
                    )}

                    {packMode === "carton" && (
                        <>
                            <div className="pack-sticky-top">
                                <div className="pack-ready-banner">
                                    <div className="pack-ready-copy">
                                        <span className="pack-ready-kicker">Carton packing</span>
                                        <strong className="pack-ready-count">{looseRows.length} invoices awaiting carton</strong>
                                        <span className="pack-ready-sub">Pick a carton number → tick from the packed list → Add → Seal</span>
                                    </div>
                                    <div className="pack-carton-meter">
                                        <span className="pack-carton-label">In cartons / packed</span>
                                        <strong className="pack-carton-val">{inCartonCount} / {inCartonCount + looseRows.length}</strong>
                                        <div className="pack-carton-bar">
                                            <div className="pack-carton-fill" style={{ width: `${cartonProgressPct}%` }} />
                                        </div>
                                    </div>
                                    <div className="pack-carton-meter pack-carton-meter--target">
                                        <span className="pack-carton-label">How many per carton?</span>
                                        <div className="pack-partial-target-row">
                                            <input
                                                type="number"
                                                className="pack-partial-target-input"
                                                min={1}
                                                max={999}
                                                value={cartonTargetDraft === "" || cartonTargetDraft == null ? "" : cartonTargetDraft}
                                                placeholder={String(DEFAULT_CARTON_TARGET)}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    if (v === "") { setCartonTargetDraft(""); return; }
                                                    const n = parseInt(v, 10);
                                                    if (Number.isFinite(n)) setCartonTargetDraft(n);
                                                }}
                                                onBlur={(e) => saveCartonTarget(e.target.value || cartonTarget)}
                                                onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                                                aria-label="Invoices per carton"
                                            />
                                        </div>
                                        <span className="pack-partial-target-hint">25 / 30 — your call. No limit.</span>
                                    </div>
                                </div>
                            </div>

                            <section className="pack-carton-work">
                                <div className="pack-carton-picker">
                                    <span className="pack-carton-picker-label">Open cartons</span>
                                    <div className="pack-carton-picker-chips">
                                        {openCartons.length === 0 && (
                                            <span className="pack-carton-picker-empty">No carton open — create one →</span>
                                        )}
                                        {openCartons.map((c) => (
                                            <button
                                                key={c.cartonNo}
                                                type="button"
                                                className={`pack-carton-pick ${activeCartonNo === c.cartonNo ? "is-on" : ""}`}
                                                onClick={() => setActiveCartonNo(c.cartonNo)}
                                            >
                                                #{c.cartonNo}<em>{c.items.length}</em>
                                            </button>
                                        ))}
                                        <div className="pack-carton-new">
                                            <input
                                                type="number"
                                                min={1}
                                                max={999}
                                                placeholder={String(nextFreeCartonNo)}
                                                value={newCartonNo}
                                                onChange={(e) => setNewCartonNo(e.target.value)}
                                                onKeyDown={(e) => { if (e.key === "Enter") createCarton(); }}
                                                aria-label="New carton number"
                                            />
                                            <button type="button" className="pack-btn pack-btn--sec" onClick={createCarton} disabled={saving}>
                                                + Carton
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {activeCarton ? (
                                    <div className="pack-carton-block pack-carton-current">
                                        <div className="pack-carton-block-head">
                                            <strong>Carton #{activeCarton.cartonNo}</strong>
                                            <span>{activeCarton.items.length} invoice · {activeCarton.pieces} pcs</span>
                                            {activeCarton.items.length > 0 && (
                                                <button
                                                    type="button"
                                                    className="pack-carton-seal-btn"
                                                    onClick={() => sealCartonNo(activeCarton.cartonNo)}
                                                    disabled={saving}
                                                >
                                                    Seal #{activeCarton.cartonNo}
                                                </button>
                                            )}
                                        </div>
                                        {activeCarton.items.length === 0 ? (
                                            <div className="pack-carton-empty">Empty — pick invoices below and press “Add”.</div>
                                        ) : (
                                            <div className="pack-carton-chips">
                                                {activeCarton.items.map((e) => (
                                                    <span
                                                        className={`pack-carton-chip ${e.complete ? "" : "pack-carton-chip--partial"}`}
                                                        key={e.invoiceNo}
                                                        title={`${e.customerName} · ${e.pieces} pcs`}
                                                    >
                                                        <InvNo value={e.invoiceNo} />
                                                        {!e.complete && (
                                                            <i className="pack-chip-miss"> −{(e.missing || []).join("/") || "incomplete"}</i>
                                                        )}
                                                        <button
                                                            type="button"
                                                            className="pack-carton-chip-x"
                                                            onClick={() => removeFromCarton(e)}
                                                            disabled={saving}
                                                            aria-label={`Remove ${e.invoiceNo} from carton`}
                                                        >
                                                            ×
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="pack-carton-empty">Pick a carton above, or type a number and press “+ Carton”.</div>
                                )}

                                {splitRows.length > 0 && (
                                    <div className="pack-split-strip">
                                        <div className="pack-hold-head">
                                            <strong>Packed after the carton was filled ({splitRows.length})</strong>
                                            <span>
                                                These invoices are already counted in a carton, so they are not in the
                                                list below — but a piece packed later is still lying loose.
                                            </span>
                                        </div>
                                        <div className="pack-split-rows">
                                            {splitRows.map((r) => {
                                                const sealed = cartons.find((c) => c.cartonNo === r.carton)?.sealed;
                                                return (
                                                    <div className="pack-split-row" key={`sp-${r.invoiceNo}`}>
                                                        <strong><InvNo value={r.invoiceNo} /></strong>
                                                        <span className="pack-split-loose">{r.looseTypes.join(", ")}</span>
                                                        <span className="pack-split-where">
                                                            {r.cartonPieces} pcs in carton #{r.carton}
                                                            {sealed ? " (sealed)" : ""}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className="pack-btn pack-btn--pri pack-split-btn"
                                                            onClick={() => attachLooseToCarton(r)}
                                                            disabled={saving}
                                                        >
                                                            Add to #{r.carton}
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {heldRows.length > 0 && (
                                    <div className="pack-hold-strip">
                                        <div className="pack-hold-head">
                                            <strong>On hold — check before carton ({heldRows.length})</strong>
                                            <span>These stay out of every carton until you clear them.</span>
                                        </div>
                                        <div className="pack-carton-chips">
                                            {heldRows.map((r) => (
                                                <span className="pack-carton-chip pack-carton-chip--held" key={`h-${r.invoiceNo}`}>
                                                    <InvNo value={r.invoiceNo} />
                                                    <button
                                                        type="button"
                                                        className="pack-hold-clear"
                                                        onClick={() => toggleHold(r)}
                                                        disabled={saving}
                                                        aria-label={`Clear hold on ${r.invoiceNo}`}
                                                    >
                                                        Checked ✓
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="pack-carton-pool">
                                    <div className="pack-carton-pool-head">
                                        <strong>
                                            Packed — awaiting carton ({filteredLoose.length})
                                            {heldRows.length > 0 && <i className="pack-hold-count"> · {heldRows.length} on hold</i>}
                                        </strong>
                                        <input
                                            type="search"
                                            className="pack-search pack-carton-pool-search"
                                            placeholder="Invoice search — 1 = only IITKAN1"
                                            value={cartonSearch}
                                            onChange={(e) => setCartonSearch(e.target.value)}
                                            autoComplete="off"
                                        />
                                        <div className="pack-carton-pool-actions">
                                            <button type="button" onClick={() => pickFirstN(cartonTarget)}>First {cartonTarget}</button>
                                            <button type="button" onClick={() => setCartonPick(new Set(selectableLoose.map((r) => r.invoiceNo)))}>All</button>
                                            <button type="button" onClick={() => setCartonPick(new Set())}>Clear</button>
                                        </div>
                                    </div>
                                    {filteredLoose.length === 0 ? (
                                        <div className="pack-type-empty">
                                            {looseRows.length === 0 ? "All packed stock is already in cartons." : "Nothing matches this search."}
                                        </div>
                                    ) : (
                                        <div className="pack-carton-chips pack-carton-chips--pick">
                                            {filteredLoose.map((r) => {
                                                const held = isHeld(r.invoiceNo);
                                                return (
                                                    <span
                                                        key={r.invoiceNo}
                                                        className={`pack-pick-wrap ${cartonPick.has(r.invoiceNo) ? "is-on" : ""} ${held ? "is-held" : ""} ${r.complete ? "" : "is-adhoora"}`}
                                                    >
                                                        <button
                                                            type="button"
                                                            className="pack-pick-body"
                                                            onClick={() => toggleCartonPick(r.invoiceNo)}
                                                            title={`${r.customerName} · ${r.pieces} pcs${r.complete ? "" : " · incomplete"}${held ? " · ON HOLD" : ""}`}
                                                        >
                                                            <InvNo value={r.invoiceNo} />
                                                            {!r.complete && (
                                                                <i className="pack-chip-miss"> −{(r.missing || []).join("/") || "incomplete"}</i>
                                                            )}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="pack-pick-flag"
                                                            onClick={() => toggleHold(r)}
                                                            disabled={saving}
                                                            title={held ? "Clear hold — allow into a carton" : "Hold — keep out of cartons until checked"}
                                                            aria-label={held ? `Clear hold on ${r.invoiceNo}` : `Hold ${r.invoiceNo} for re-check`}
                                                        >
                                                            {held ? "⚑" : "⚐"}
                                                        </button>
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {sealedCartons.length > 0 && (
                                    <details className="pack-partial-history">
                                        <summary>Sealed cartons ({sealedCartons.length})</summary>
                                        {sealedCartons.map((c) => (
                                            <div className="pack-carton-block" key={`sc-${c.cartonNo}`}>
                                                <div className="pack-carton-block-head">
                                                    <strong>Carton #{c.cartonNo}</strong>
                                                    <span>{c.items.length} invoice · {c.pieces} pcs</span>
                                                    <button
                                                        type="button"
                                                        className="pack-carton-seal-btn pack-carton-unseal-btn"
                                                        onClick={() => unsealCartonNo(c.cartonNo)}
                                                        disabled={saving}
                                                    >
                                                        Unseal
                                                    </button>
                                                </div>
                                                <div className="pack-carton-chips">
                                                    {c.items.map((e) => (
                                                        <span
                                                            className={`pack-carton-chip ${e.complete ? "" : "pack-carton-chip--partial"}`}
                                                            key={e.invoiceNo}
                                                            title={`${e.customerName} · ${e.pieces} pcs`}
                                                        >
                                                            <InvNo value={e.invoiceNo} />
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </details>
                                )}

                                {legacyPartialCartons.length > 0 && (
                                    <details className="pack-partial-history">
                                        <summary>Old partial cartons ({legacyPartialCartons.length}) — record only</summary>
                                        {legacyPartialCartons.map((c) => (
                                            <div className="pack-carton-block" key={`lp-${c.cartonNo}`}>
                                                <div className="pack-carton-block-head">
                                                    <strong>Partial #{c.cartonNo}</strong>
                                                    <span>{c.items.length} invoice · {c.pieces} pcs</span>
                                                </div>
                                                <div className="pack-carton-chips">
                                                    {c.items.map((e) => (
                                                        <span className="pack-carton-chip pack-carton-chip--partial" key={e.invoiceNo} title={`Missing: ${(e.missing || []).join(", ") || "—"}`}>
                                                            <InvNo value={e.invoiceNo} />
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </details>
                                )}
                            </section>

                            <div className="pack-dock pack-dock--carton">
                                <button
                                    type="button"
                                    className="pack-btn pack-btn--pri"
                                    disabled={saving || !activeCarton || cartonPick.size === 0}
                                    onClick={addPickedToCarton}
                                >
                                    {activeCarton ? `Add to carton #${activeCarton.cartonNo}` : "Pick a carton first"} ({cartonPick.size})
                                </button>
                                <button
                                    type="button"
                                    className="pack-btn pack-btn--sec"
                                    disabled={saving || !activeCarton || activeCarton.items.length === 0}
                                    onClick={() => activeCarton && sealCartonNo(activeCarton.cartonNo)}
                                >
                                    Seal
                                </button>
                                <span className="pack-dock-note">
                                    {cartonPick.size} selected{activeCarton ? ` · #${activeCarton.cartonNo} has ${activeCarton.items.length}` : ""} · target {cartonTarget}
                                </span>
                            </div>
                        </>
                    )}
                </>
            )}

            {missingModal && (
                <div className="pack-modal-bg" onClick={closeMissingModal}>
                    <div className="pack-modal pack-detail-view" onClick={(e) => e.stopPropagation()}>
                        <div className="pack-missing-modal-head">
                            <div>
                                <h3>Full detail — what is missing</h3>
                                <p className="pack-missing-modal-sub">
                                    {driveOn
                                        ? `${drive.campus || "All"}${drive.from ? ` · ${fmtDriveRange(drive.from, drive.to)}` : ""}`
                                        : "All campuses"}
                                    {missingSearch.trim() ? " · filtered" : ""}
                                    {detailType ? ` · ${detailType} only` : ""}
                                    {" · ties not counted"}
                                </p>
                            </div>
                            <button type="button" className="pack-missing-modal-x" onClick={closeMissingModal} aria-label="Close">×</button>
                        </div>

                        <input
                            type="search"
                            className="pack-missing-search"
                            placeholder="Invoice, name, tailor or garment — 1 = only IITKAN1"
                            value={missingSearch}
                            onChange={(e) => setMissingSearch(e.target.value)}
                            autoComplete="off"
                        />

                        {pendingDetail.typeFilters.length > 1 && (
                            <div className="pack-detail-filter" role="group" aria-label="Garment filter">
                                <button
                                    type="button"
                                    className={`pack-detail-filter-chip ${detailType === "" ? "is-on" : ""}`}
                                    onClick={() => setDetailType("")}
                                >
                                    All<em>{pendingDetail.searchedTotal}</em>
                                </button>
                                {pendingDetail.typeFilters.map((t) => (
                                    <button
                                        key={t.type}
                                        type="button"
                                        className={`pack-detail-filter-chip ${detailType === t.type ? "is-on" : ""}`}
                                        onClick={() => setDetailType(detailType === t.type ? "" : t.type)}
                                    >
                                        {t.type}<em>{t.count}</em>
                                    </button>
                                ))}
                            </div>
                        )}

                        <div className="pack-detail-tiles">
                            <div className="pack-detail-tile">
                                <span>Missing pieces</span>
                                <strong>{pendingDetail.total}</strong>
                                <em>across {pendingDetail.orders} invoices</em>
                            </div>
                            <div className="pack-detail-tile is-tailor">
                                <span>With tailors</span>
                                <strong>{pendingDetail.withTailor}</strong>
                                <em>{pendingDetail.byTailor.length} tailors</em>
                            </div>
                            <div className="pack-detail-tile is-none">
                                <span>Not issued to anyone</span>
                                <strong>{pendingDetail.notIssued}</strong>
                                <em>still to distribute</em>
                            </div>
                        </div>

                        {pendingDetail.total === 0 ? (
                            <div className="pack-missing-modal-empty">
                                {detailType
                                    ? `No ${detailType.toLowerCase()} missing${missingSearch.trim() ? ` for “${missingSearch.trim()}”` : ""}.`
                                    : missingSearch.trim()
                                        ? `Nothing matches “${missingSearch.trim()}”.`
                                        : "Everything for this drive has arrived — nothing missing."}
                            </div>
                        ) : (
                            <>
                                <h4 className="pack-detail-section-title">Missing by fabric</h4>
                                <div className="pack-detail-types">
                                    {pendingDetail.byFabric.map((g) => (
                                        <details className={`pack-detail-type ${g.unknown ? "is-unknown" : ""}`} key={g.label}>
                                            <summary>
                                                <strong className="pack-detail-fab">{g.label}</strong>
                                                <span className="pack-detail-type-num">{g.count}</span>
                                                {!detailType && (
                                                    <em>{g.types.map(([ty, n]) => `${ty} ${n}`).join(" · ")}</em>
                                                )}
                                                <span className="pack-detail-who">
                                                    {g.tailors.map(([who, n]) => (
                                                        <i
                                                            className={who === NOT_ISSUED ? "is-none" : ""}
                                                            key={who}
                                                        >
                                                            {who}<b>{n}</b>
                                                        </i>
                                                    ))}
                                                </span>
                                            </summary>
                                            <div className="pack-detail-chips">
                                                {g.pieces.map((p) => (
                                                    <button
                                                        key={p.key}
                                                        type="button"
                                                        className={`pack-detail-chip ${p.notIssued ? "is-none" : ""}`}
                                                        onClick={() => openMissingOrder(p.bookingId)}
                                                        title={`${p.customerName} · ${p.type} · ${p.where}`}
                                                    >
                                                        <em><InvNo value={p.invoiceNo} /></em>
                                                        <span>{p.type}{p.tailor ? ` · ${p.tailor}` : ""}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </details>
                                    ))}
                                </div>

                                <h4 className="pack-detail-section-title">Missing by garment</h4>
                                <div className="pack-detail-types">
                                    {pendingDetail.byType.map((t) => (
                                        <details className="pack-detail-type" key={t.type}>
                                            <summary>
                                                <strong>{t.type}</strong>
                                                <span className="pack-detail-type-num">{t.count}</span>
                                                <em>
                                                    {t.withTailor} with tailors
                                                    {t.notIssued > 0 ? ` · ${t.notIssued} not issued` : ""}
                                                </em>
                                            </summary>
                                            <div className="pack-detail-chips">
                                                {t.pieces.map((p) => (
                                                    <button
                                                        key={p.key}
                                                        type="button"
                                                        className={`pack-detail-chip ${p.notIssued ? "is-none" : ""}`}
                                                        onClick={() => openMissingOrder(p.bookingId)}
                                                        title={`${p.customerName} · ${p.fabricLabel} · ${p.where}`}
                                                    >
                                                        <em><InvNo value={p.invoiceNo} /></em>
                                                        <span>{p.fabricLabel}</span>
                                                        <i>{p.tailor || "not issued"}</i>
                                                    </button>
                                                ))}
                                            </div>
                                        </details>
                                    ))}
                                </div>

                                {pendingDetail.byTailor.length > 0 && (
                                    <>
                                        <h4 className="pack-detail-section-title">Who has them</h4>
                                        <div className="pack-detail-tailors">
                                            {pendingDetail.byTailor.map((g) => (
                                                <details className="pack-detail-tailor" key={g.tailor}>
                                                    <summary>
                                                        <strong>{g.tailor}</strong>
                                                        <span className="pack-detail-type-num">{g.count}</span>
                                                        <em>{g.types.map(([ty, n]) => `${ty} ${n}`).join(" · ")}</em>
                                                    </summary>
                                                    <div className="pack-detail-stages">
                                                        {g.stages.map(([st, n]) => (
                                                            <span className="pack-detail-stage" key={st}>{st}<i>{n}</i></span>
                                                        ))}
                                                    </div>
                                                    <div className="pack-detail-chips">
                                                        {g.pieces.map((p) => (
                                                            <button
                                                                key={p.key}
                                                                type="button"
                                                                className="pack-detail-chip"
                                                                onClick={() => openMissingOrder(p.bookingId)}
                                                                title={`${p.customerName} · ${p.fabricLabel} · ${p.where}`}
                                                            >
                                                                <em><InvNo value={p.invoiceNo} /></em>
                                                                <span>{p.fabricLabel}</span>
                                                                <i>{p.type}</i>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </details>
                                            ))}
                                        </div>
                                    </>
                                )}

                                {pendingDetail.notIssuedPieces.length > 0 && (
                                    <>
                                        <h4 className="pack-detail-section-title pack-detail-section-title--warn">
                                            Not issued to anyone ({pendingDetail.notIssuedPieces.length})
                                        </h4>
                                        <div className="pack-detail-chips">
                                            {pendingDetail.notIssuedPieces.map((p) => (
                                                <button
                                                    key={p.key}
                                                    type="button"
                                                    className="pack-detail-chip is-none"
                                                    onClick={() => openMissingOrder(p.bookingId)}
                                                    title={`${p.customerName} · ${p.fabricLabel}`}
                                                >
                                                    <em><InvNo value={p.invoiceNo} /></em>
                                                    <span>{p.fabricLabel}</span>
                                                    <i>{p.type}</i>
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                )}

                                {filteredMissingReport.orderRows.length > 0 && (
                                    <details className="pack-detail-half">
                                        <summary>
                                            Part-arrived orders ({filteredMissingReport.orderRows.length})
                                        </summary>
                                        <ul className="pack-missing-modal-list">
                                            {filteredMissingReport.orderRows.map((row) => (
                                                <li key={row.bookingId}>
                                                    <button
                                                        type="button"
                                                        className="pack-missing-modal-row"
                                                        onClick={() => openMissingOrder(row.bookingId)}
                                                    >
                                                        <div className="pack-missing-modal-row-top">
                                                            <strong><InvNo value={row.invoiceNo} /></strong>
                                                            <span>{row.customerName}</span>
                                                        </div>
                                                        <div className="pack-missing-modal-row-parts">
                                                            <span className="pack-missing-got">✓ {row.arrived.join(" · ")}</span>
                                                            <span className="pack-missing-need">✗ {row.pending.join(" · ")}</span>
                                                        </div>
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </details>
                                )}

                                {!!missingReport.typeSummary.length && (
                                    <details className="pack-missing-warehouse-fold">
                                        <summary>Warehouse totals (all orders in this drive)</summary>
                                        <div className="pack-missing-summary">
                                            {missingReport.typeSummary.map((row) => (
                                                <div
                                                    key={row.type}
                                                    className={`pack-missing-summary-card ${row.pending > 0 ? "is-short" : ""}`}
                                                >
                                                    <span className="pack-missing-summary-type">{row.type}</span>
                                                    <strong className="pack-missing-summary-num">{row.received}<i>/{row.total}</i></strong>
                                                    <span className="pack-missing-summary-meta">
                                                        {row.pending > 0 ? `${row.pending} pending` : "all in"}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </details>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}

            {confirmState && (
                <div className="pack-modal-bg" onClick={() => resolveConfirm(false)}>
                    <div className="pack-modal pack-confirm" onClick={(e) => e.stopPropagation()}>
                        <h3 className="pack-confirm-title">{confirmState.title}</h3>
                        <div className="pack-confirm-body">{confirmState.body}</div>
                        <div className="pack-modal-actions">
                            <button type="button" className="pack-btn pack-btn--sec" onClick={() => resolveConfirm(false)}>Cancel</button>
                            <button
                                type="button"
                                className={`pack-btn ${confirmState.tone === "danger" ? "pack-btn--danger" : "pack-btn--pri"}`}
                                onClick={() => resolveConfirm(true)}
                            >
                                {confirmState.confirmLabel || "Confirm"}
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {toast && (
                <div className={`pack-toast pack-toast--${toast.kind}${toast.canUndo ? " pack-toast--undo" : ""}`} role="status">
                    <span className="pack-toast-msg">{toast.msg}</span>
                    {toast.canUndo && (
                        <button type="button" className="pack-toast-undo" onClick={undoLast} disabled={saving}>
                            ↩ Undo
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export default Campus_packing;
